/* global Excel, Office */
import { sandboxedEval } from './sandbox';
import {
  assertFormulasAllowed,
  assertCodeFreeOfBlockedFormulas,
  assertFormulaAssignmentAllowed,
} from './formulaSecurity';

/**
 * ExcelBridge - Office.js code execution engine for Excel
 *
 * Core mechanism: The backend Claude agent generates Office.js JavaScript code
 * and structured tool calls. This bridge evaluates that code inside Excel.run()
 * context, and implements the structured spreadsheet tools (read, write,
 * structure, objects) directly against the Excel object model.
 *
 * It is the Excel analogue of PowerPointBridge: the SES sandbox, the SSE
 * tool-result loop, and the context-injection site all drop in unchanged
 * because the constructor shape, the `_safeOfficeRun` wrapper, the
 * `executeCode` SES runner, and `getInitialState()` / `getUserContext()` all
 * keep the same names and return-shape conventions.
 *
 * Worksheets are identified by `tabId` (a stable numeric id), which is the
 * Excel analogue to PowerPoint's slide index. Index- and name-only lookups are
 * avoided because they break when sheets are moved or renamed.
 *
 * Tools implemented (matching Anthropic's Claude for Excel):
 *  - executeCode(code)                  -> eval arbitrary Office.js inside Excel.run()
 *  - getCellRanges({ ... })             -> read values/formulas/styles/source comments
 *  - screenshotRange({ ... })           -> render a range as PNG for visual QA
 *  - getRangeAsCsv({ ... })             -> read a range as a CSV string for analysis
 *  - searchData({ ... })                -> native cell search across the workbook
 *  - getAllObjects({ ... })             -> list charts and pivot tables
 *  - setCellRange({ ... })              -> write values/formulas/styles (+ overwrite guard)
 *  - clearCellRange({ ... })            -> clear contents / formats / all
 *  - copyTo({ ... })                    -> copy a range with formula translation
 *  - modifySheetStructure({ ... })      -> insert/delete/hide/freeze rows and columns
 *  - modifyWorkbookStructure({ ... })   -> create/delete/rename/duplicate sheets
 *  - resizeRange({ ... })               -> adjust column widths and row heights
 *  - modifyObject({ ... })              -> create/update/delete charts and pivot tables
 *  - getInitialState()                  -> workbook metadata for context injection
 *  - getUserContext()                   -> active sheet and current selection
 */

// =============================================================================
// CONSTANTS
// =============================================================================

/** Maximum addressable Excel grid (rows x columns). */
const MAX_EXCEL_ROWS = 1_048_576;

/** Default cap on non-empty cells returned by getCellRanges. */
const DEFAULT_CELL_LIMIT = 2000;

/** Default cap on rows returned by getRangeAsCsv. */
const DEFAULT_CSV_MAX_ROWS = 500;

/** Default page size for searchData. */
const DEFAULT_SEARCH_MAX_RESULTS = 500;

/** Chunk size for getSheet-style row reads (matches the reference bridge). */
const ROW_CHUNK_SIZE = 1000;

/** Hard bound for a single formula-copy operation and its safety readback. */
const MAX_COPY_OPERATION_CELLS = 50_000;

/** Maximum cells loaded in one copy conflict or formula readback page. */
const COPY_READ_PAGE_CELLS = 5_000;
const DALOOPA_CITATION_ID_RE = /^cx_[A-Za-z0-9_-]{8,128}$/;
const DALOOPA_CITATION_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z)?$/;
const DALOOPA_DATASET_BY_QUERY_CLASS = Object.freeze({
  company_lookup: 'Company Directory',
  series_discovery: 'Financial Series Catalog',
  document_discovery: 'Company Documents',
  fundamentals: 'Company Fundamentals',
  document_content: 'Company Documents',
  prices: 'Market Prices',
  document_search: 'Company Documents',
});

function validateDaloopaCitations(citations) {
  if (!Array.isArray(citations) || citations.length < 1 || citations.length > 100) {
    throw new Error('One to 100 Daloopa citations are required');
  }
  const seenIds = new Set();
  return citations.map((citation) => {
    const isRecord = citation !== null && typeof citation === 'object' && !Array.isArray(citation);
    const asOfDateIsValid = citation?.asOfDate === null || (
      typeof citation?.asOfDate === 'string' &&
      citation.asOfDate.length <= 40 &&
      DALOOPA_CITATION_DATE_RE.test(citation.asOfDate) &&
      Number.isFinite(Date.parse(citation.asOfDate))
    );
    const retrievedAtIsValid = typeof citation?.retrievedAt === 'string' &&
      citation.retrievedAt.length <= 40 &&
      DALOOPA_CITATION_DATE_RE.test(citation.retrievedAt) &&
      Number.isFinite(Date.parse(citation.retrievedAt));
    const expectedDataset = DALOOPA_DATASET_BY_QUERY_CLASS[citation?.queryClass];
    const displayDate = (citation?.asOfDate || citation?.retrievedAt || '').slice(0, 10);
    const expectedMarker = isRecord
      ? `Source: Daloopa | ${citation.dataset} | ${citation.queryClass} | ${displayDate} | ${citation.citationId}`
      : '';
    if (
      !isRecord ||
      citation.providerName !== 'Daloopa' ||
      !DALOOPA_CITATION_ID_RE.test(citation.citationId || '') ||
      seenIds.has(citation.citationId) ||
      !expectedDataset ||
      citation.dataset !== expectedDataset ||
      !asOfDateIsValid ||
      !retrievedAtIsValid ||
      citation.marker !== expectedMarker ||
      citation.marker.length > 360
    ) {
      throw new Error('Daloopa citation metadata is invalid');
    }
    seenIds.add(citation.citationId);
    return Object.freeze({
      citationId: citation.citationId,
      marker: expectedMarker,
    });
  });
}

function validateDaloopaExcelTarget(target) {
  const keys = target && typeof target === 'object' && !Array.isArray(target)
    ? Object.keys(target).sort().join(',')
    : '';
  const sheetName = target?.sheet_name;
  const anchorCell = target?.anchor_cell;
  const hasInvalidSheetNameChar = typeof sheetName === 'string' && [...sheetName].some((character) =>
    character.charCodeAt(0) < 32 || '\\/?*:[]'.includes(character)
  );
  if (
    keys !== 'anchor_cell,app,sheet_name' ||
    target.app !== 'excel' ||
    typeof sheetName !== 'string' ||
    sheetName.length < 1 ||
    sheetName.length > 31 ||
    sheetName.trim() !== sheetName ||
    hasInvalidSheetNameChar ||
    typeof anchorCell !== 'string' ||
    !/^[A-Z]{1,3}[1-9]\d{0,6}$/.test(anchorCell)
  ) {
    throw new Error('A valid Excel citation target is required');
  }
  const parsed = parseA1(anchorCell);
  if (parsed.row > MAX_EXCEL_ROWS || parsed.col > 16_384) {
    throw new Error('A valid Excel citation target is required');
  }
  return Object.freeze({ sheetName, anchorCell });
}

function validateFinanceExcelTableRequest(args) {
  const keys = args && typeof args === 'object' && !Array.isArray(args)
    ? Object.keys(args)
    : [];
  if (
    keys.length < 2 ||
    keys.some((key) => !['target', 'cells', 'allow_overwrite'].includes(key)) ||
    !keys.includes('target') ||
    !keys.includes('cells') ||
    (args.allow_overwrite !== undefined && typeof args.allow_overwrite !== 'boolean')
  ) {
    throw new Error('Finance Excel table input is invalid');
  }
  const target = validateDaloopaExcelTarget(args.target);
  if (!Array.isArray(args.cells) || args.cells.length < 1) {
    throw new Error('Finance Excel table cells must be a non-empty rectangle');
  }
  const columnCount = Array.isArray(args.cells[0]) ? args.cells[0].length : 0;
  if (
    columnCount < 1 ||
    args.cells.length * columnCount > 5000 ||
    args.cells.some((row) => !Array.isArray(row) || row.length !== columnCount)
  ) {
    throw new Error('Finance Excel table cells must be a non-empty rectangle of at most 5000 cells');
  }

  const cells = args.cells.map((row) => row.map((value) => {
    const scalar = value === null || typeof value === 'boolean' ||
      (typeof value === 'number' && Number.isFinite(value)) ||
      (typeof value === 'string' && value.length <= 2000);
    if (!scalar) throw new Error('Finance Excel table cells must contain bounded JSON scalar values');
    if (typeof value === 'string' && /^(?:=|\+|-|@|\{=)/.test(value.trim())) {
      throw new Error('Finance Excel table cells cannot contain formulas');
    }
    return value;
  }));

  const anchor = parseA1(target.anchorCell);
  const endRow = anchor.row + cells.length - 1;
  const endColumn = anchor.col + columnCount - 1;
  if (endRow > MAX_EXCEL_ROWS || endColumn > 16_384) {
    throw new Error('Finance Excel table exceeds worksheet bounds');
  }
  const endCell = `${columnToLetter(endColumn)}${endRow}`;
  const rangeAddress = endCell === target.anchorCell
    ? target.anchorCell
    : `${target.anchorCell}:${endCell}`;
  return Object.freeze({
    target,
    cells,
    rowCount: cells.length,
    columnCount,
    rangeAddress,
    allowOverwrite: args.allow_overwrite === true,
  });
}

function excelScalarMatches(expected, actual) {
  if (expected === null) return actual === '' || actual === null;
  return Object.is(expected, actual);
}

// =============================================================================
// STYLE MAPS (string enum value -> Excel enum), resolved lazily at call time
// =============================================================================

// Tool-enum chart-type names that do NOT match an Excel.ChartType key directly
// (3D variants, scatter, ...). Everything not listed resolves by direct lookup.
const CHART_TYPE_ALIASES = {
  column3D: '_3DColumnClustered',
  bar3D: '_3DBarClustered',
  line3D: '_3DLine',
  area3D: '_3DArea',
  pie3D: '_3DPie',
  scatter: 'xyscatter',
  scatterLines: 'xyscatterLinesNoMarkers',
  scatterLinesMarkers: 'xyscatterLines',
};

// Tool-enum summarizeBy names (OOXML ST_DataConsolidateFunction) that differ
// from the Excel.AggregationFunction keys.
const SUMMARIZE_ALIASES = {
  countNums: 'countNumbers',
  stdDev: 'standardDeviation',
  stdDevp: 'standardDeviationP',
  var: 'variance',
  varp: 'varianceP',
};

/**
 * Map a chart-type string (tool enum) to the Excel.ChartType enum value. Tries
 * the alias table first (for names that differ), then a direct key lookup, then
 * falls back to columnClustered. The runtime check against the live enum means
 * an unsupported type degrades gracefully instead of throwing.
 * @param {string} chartType - Chart type string from the tool input
 * @returns {string} Excel.ChartType value
 */
function mapChartType(chartType) {
  const types = Excel.ChartType || {};
  const alias = CHART_TYPE_ALIASES[chartType];
  if (alias && types[alias] != null) return types[alias];
  if (chartType && types[chartType] != null) return types[chartType];
  return types.columnClustered != null ? types.columnClustered : 'ColumnClustered';
}

/**
 * Map a summarizeBy string (tool enum) to Excel.AggregationFunction. Handles the
 * OOXML aliases (countNums/stdDev/var/...) then a direct lookup, defaulting to sum.
 * @param {string} summarizeBy - Aggregation name (sum, count, average, ...)
 * @returns {string} Excel.AggregationFunction value
 */
function mapSummarizeBy(summarizeBy) {
  const fns = Excel.AggregationFunction || {};
  const alias = SUMMARIZE_ALIASES[summarizeBy];
  if (alias && fns[alias] != null) return fns[alias];
  if (summarizeBy && fns[summarizeBy] != null) return fns[summarizeBy];
  return fns.sum != null ? fns.sum : 'Sum';
}

// =============================================================================
// TIMEOUT GUARD (identical contract to PowerPointBridge)
// =============================================================================

const TOOL_TIMEOUT_MS = 80_000; // 80s - under backend's 90s timeout

/**
 * Race a promise against a timeout. If the promise doesn't resolve
 * within the given ms, reject with a descriptive error.
 * @param {Promise} promise - The promise to race
 * @param {number} ms - Timeout in milliseconds
 * @param {string} label - Label for the error message
 * @returns {Promise} - Resolves with the original promise or rejects on timeout
 */
function withTimeout(promise, ms, label = 'Office.js call') {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms / 1000}s - try a simpler operation or break it into smaller steps`));
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function isSesInitializationError(error) {
  const message = String(error?.message || error || '');
  return message.includes('Compartment') ||
    message.includes('lockdown') ||
    message.includes('SES');
}

function createSesUnavailableError(toolName) {
  return new Error(
    `SES sandbox unavailable - ${toolName} is disabled until the sandbox initializes successfully`
  );
}

// =============================================================================
// UTILITY: A1 <-> index math
// =============================================================================

/**
 * Convert a 1-based column number to its letter (1 -> A, 27 -> AA).
 * @param {number} col - 1-based column number
 * @returns {string} Column letters
 */
function columnToLetter(col) {
  let result = '';
  let n = col;
  while (n > 0) {
    n--;
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26);
  }
  return result;
}

/**
 * Convert column letters to a 1-based column number (A -> 1, AA -> 27).
 * @param {string} letter - Column letters
 * @returns {number|null} 1-based column number, or null if invalid
 */
function letterToColumn(letter) {
  const upper = String(letter || '').toUpperCase();
  if (!/^[A-Z]+$/.test(upper)) return null;
  let col = 0;
  for (let i = 0; i < upper.length; i++) {
    const code = upper.charCodeAt(i) - 64;
    if (code < 1 || code > 26) return null;
    col = col * 26 + code;
  }
  return col || null;
}

/**
 * Parse a single A1 cell reference (e.g. "B7") into 1-based row/col numbers.
 * @param {string} a1 - A1 cell reference (no sheet prefix, no range)
 * @returns {{ row: number, col: number }}
 */
function parseA1(a1) {
  const match = String(a1 || '').match(/^\$?([A-Z]+)\$?(\d+)$/i);
  if (!match) throw new Error(`Invalid A1 notation: ${a1}`);
  const col = letterToColumn(match[1]);
  if (!col) throw new Error(`Invalid A1 notation: ${a1}`);
  return { row: parseInt(match[2], 10), col };
}

/**
 * Preserve an existing threaded comment while adding a source citation.
 * Repeated writes are idempotent, and user-authored comment text is never
 * replaced by a note-field write.
 */
export function mergeSourceCommentContent(existingContent, sourceContent) {
  const existing = String(existingContent || '').trim();
  const source = String(sourceContent || '').trim();
  if (!existing) return source;
  if (!source || existing.includes(source)) return existing;
  return `${existing}\n\n${source}`;
}

function normalizeCommentContent(content) {
  return String(content || '').trim();
}

function commentThreadHasExactContent(thread, content) {
  const expected = normalizeCommentContent(content);
  if (!expected || !thread) return false;
  return [thread.rootContent, ...(thread.replyContents || [])]
    .some((candidate) => normalizeCommentContent(candidate) === expected);
}

function formatCommentThread(thread) {
  if (!thread) return '';
  return [thread.rootContent, ...(thread.replyContents || [])]
    .map(normalizeCommentContent)
    .filter(Boolean)
    .join('\n\n');
}

// =============================================================================
// FORMULA WRITE-BOUNDARY SCREEN (runtime, for execute_office_js)
// =============================================================================

// Range setters whose value is screened for blocked formulas at write time.
// B1: `values`/`valuesAsJson` are included because Excel evaluates a string
// written to `.values` that begins with = / + / - / @ as a live formula, so
// {value:"=WEBSERVICE(...)"} written via .values would otherwise exfiltrate on
// recalc, bypassing the formula screen. assertFormulaAssignmentAllowed only
// flags formula-shaped strings, so ordinary value writes pass untouched.
const SCREENED_FORMULA_SETTERS = new Set([
  'formulas',
  'formulasLocal',
  'formulasR1C1',
  'values',
  'valuesAsJson',
  'valuesAsJsonLocal'
]);

// Sentinel that both marks a screening proxy and, when read, yields the genuine
// target it wraps. Used to avoid double wrapping and to UNWRAP a screening proxy
// that AI code passes back into an Office.js method as an argument, so the host
// receives its own raw object and its internal identity checks keep working.
const FORMULA_SCREEN_MARKER = Symbol('restackFormulaScreen');

/**
 * Return the genuine target a screening proxy wraps, or the value itself if it
 * is not one. Lets the apply trap hand Office.js raw objects for its arguments.
 * @param {*} value - A possibly-wrapped value
 * @returns {*} The unwrapped target, or the value unchanged
 */
function unwrapFormulaScreen(value) {
  if (value === null || (typeof value !== 'object' && typeof value !== 'function')) {
    return value;
  }
  try {
    const raw = value[FORMULA_SCREEN_MARKER];
    return raw !== undefined ? raw : value;
  } catch {
    return value;
  }
}

/**
 * Wrap a live Excel object (the request `context` and everything navigated off
 * it) so that any assignment to a range's `formulas` / `formulasLocal` is
 * screened for blocked formula functions (=WEBSERVICE / =IMPORTDATA / =RTD /
 * DDE etc.) at the moment of the write. The static execute_office_js code scan
 * only sees formula-shaped string literals; a formula assembled at run time by
 * string concatenation is invisible to it but is fully resolved here, so this
 * setter screen closes that bypass. Rejects (throws) on a blocked formula
 * before the assignment reaches Office.js; legitimate formulas pass through.
 *
 * The wrapper is a transparent read-through Proxy: reads return the underlying
 * value (methods bound to the genuine target to preserve Office.js's `this`
 * identity and request batching, nested objects re-wrapped so the screen holds
 * at every depth) and only the two formula setters are intercepted. All other
 * writes pass straight through.
 *
 * @param {*} obj - Excel object to wrap (or any value; primitives pass through)
 * @param {WeakMap} [seen] - Cache so a given target maps to a stable wrapper
 * @returns {*} The screening wrapper, or the original value if it is a primitive
 */
function screenFormulaWrites(obj, seen = new WeakMap()) {
  if (obj === null || (typeof obj !== 'object' && typeof obj !== 'function')) {
    return obj;
  }
  if (obj[FORMULA_SCREEN_MARKER]) return obj;
  const cached = seen.get(obj);
  if (cached) return cached;

  const proxy = new Proxy(obj, {
    get(target, prop) {
      // Reading the marker yields the genuine target (for unwrapping).
      if (prop === FORMULA_SCREEN_MARKER) return target;
      const value = Reflect.get(target, prop, target);
      if (typeof value === 'function') {
        return screenFormulaWrites(value.bind(target), seen);
      }
      if (value !== null && typeof value === 'object') {
        return screenFormulaWrites(value, seen);
      }
      return value;
    },
    set(target, prop, value) {
      // Screen the resolved value at the write boundary. Fails closed: a
      // blocked formula throws here and the assignment never reaches Office.js.
      if (typeof prop === 'string' && SCREENED_FORMULA_SETTERS.has(prop)) {
        assertFormulaAssignmentAllowed(value);
      }
      return Reflect.set(target, prop, value, target);
    },
    apply(target, thisArg, args) {
      // Unwrap any wrapped arguments so Office.js receives its own raw objects
      // (e.g. range.copyFrom(otherRange)) and is not confused by our proxy.
      const rawArgs = args.map(unwrapFormulaScreen);
      // Re-wrap the call result so a Range (or any object) returned by a method
      // such as getRange / getRangeByIndexes is itself screened: otherwise a
      // formula write on a freshly-returned range would slip past the proxy,
      // which only auto-wraps values reached through property reads.
      const result = Reflect.apply(target, thisArg, rawArgs);
      // Pass Promises through unwrapped (e.g. context.sync()): they expose no
      // formula setter, so wrapping the await chain buys nothing.
      if (result && typeof result.then === 'function') return result;
      return screenFormulaWrites(result, seen);
    },
  });

  seen.set(obj, proxy);
  return proxy;
}

// =============================================================================
// MAIN CLASS
// =============================================================================

export class ExcelBridge {
  constructor({ isOfficeOnline = false } = {}) {
    this._ready = false;
    this._lastError = null;
    this._isOfficeOnline = isOfficeOnline;
    // Office Online serialization queue (1.1) - prevents concurrent Excel.run() crashes
    this._onlineRunQueue = Promise.resolve();
  }

  /**
   * Safe wrapper around Excel.run() that serializes calls on Office Online.
   * Desktop can handle concurrent calls; Office Online crashes with GeneralException.
   * @param {Function} fn - Callback receiving (context)
   * @returns {Promise<any>}
   */
  _safeOfficeRun(fn, { timeoutMs = TOOL_TIMEOUT_MS, label = 'Office.run', signal = null } = {}) {
    // Bound EVERY run with a timeout measured from when the run ACTUALLY starts
    // (after the queue clears, on Office web). This is the key to preventing
    // cascades: a slow or hung Excel.run can't consume a later call's time
    // budget, and the serialized queue always advances within `timeoutMs`.
    // (async () => ...) ensures even a synchronous throw becomes a rejection.
    const exec = () => {
      if (signal?.aborted) {
        const error = new Error(`${label} was cancelled before Office execution started`);
        error.name = 'AbortError';
        return Promise.reject(error);
      }
      return withTimeout((async () => Excel.run(fn))(), timeoutMs, label);
    };

    // Desktop: runs are reliable and may overlap safely - no serialization.
    if (!this._isOfficeOnline) {
      return exec();
    }

    // Office web: concurrent Excel.run calls crash with GeneralException, so
    // serialize them. Chain each run after the previous one SETTLES. The chain
    // NEVER rejects (so one failure can't break the queue for everyone) and
    // NEVER stays pending (exec is always timeout-bounded), so the queue
    // self-heals within `timeoutMs` even if a run hangs.
    const run = this._onlineRunQueue.then(exec, exec);
    this._onlineRunQueue = run.then(() => {}, () => {});
    return run;
  }

  async _loadCommentThreadsByCell(sheet, context) {
    const comments = sheet?.comments;
    if (!comments || typeof comments.load !== 'function') {
      throw new Error('Excel threaded comments are unavailable on this host');
    }

    comments.load('items');
    await context.sync();
    const pending = [];
    for (const comment of comments.items || []) {
      if (!comment?.replies || typeof comment.getLocation !== 'function') {
        throw new Error('Excel threaded comments are unavailable on this host');
      }
      comment.load('content');
      const location = comment.getLocation();
      location.load('rowIndex,columnIndex');
      comment.replies.load('items/content');
      pending.push({ comment, location });
    }
    if (pending.length > 0) await context.sync();

    const threads = new Map();
    for (const { comment, location } of pending) {
      if (!Number.isInteger(location.rowIndex) || !Number.isInteger(location.columnIndex)) {
        throw new Error('Excel threaded comment location could not be verified');
      }
      const address = `${columnToLetter(location.columnIndex + 1)}${location.rowIndex + 1}`;
      threads.set(address, {
        comment,
        rootContent: normalizeCommentContent(comment.content),
        replyContents: (comment.replies.items || [])
          .map((reply) => normalizeCommentContent(reply.content))
          .filter(Boolean),
      });
    }
    return threads;
  }

  // ===========================================================================
  // INTERNAL HELPERS (worksheet lookup, metadata, styles)
  // ===========================================================================

  /**
   * Find a worksheet inside a request context by its stable tabId (preferred)
   * or, as a fallback, by its name. tabId is the Excel analogue to a slide
   * index: it survives moves and renames, unlike the array position or name.
   * @param {Excel.RequestContext} context - The active request context
   * @param {number|string} sheetId - tabId (number) or sheet name (string)
   * @returns {Promise<Excel.Worksheet>} The matching worksheet
   * @throws {Error} if no worksheet matches
   */
  async _getWorksheetById(context, sheetId) {
    const worksheets = context.workbook.worksheets;
    worksheets.load('items/name,items/tabId');
    await context.sync();

    const targetId = Number(sheetId);
    const targetName = String(sheetId);
    const sheet = worksheets.items.find((ws) =>
      ws.tabId === targetId || ws.name === targetName
    );

    if (!sheet) {
      const available = worksheets.items
        .map((ws) => `${ws.name} (id ${ws.tabId})`)
        .join(', ');
      throw new Error(
        `NotFoundError: Worksheet "${sheetId}" not found. Available sheets: ${available || '(none)'}`
      );
    }
    return sheet;
  }

  /**
   * Load dimensions and frozen-pane counts for a worksheet. Uses
   * getUsedRangeOrNullObject(true) so a blank sheet reports zero size instead
   * of throwing, mirroring the reverse-engineered getSheetMetadata.
   * @param {Excel.Worksheet} sheet - Worksheet to inspect
   * @param {Excel.RequestContext} context - The active request context
   * @returns {Promise<{ sheetId: number, name: string, rows: number, cols: number, frozenRows: number, frozenCols: number }>}
   */
  async _getSheetMetadata(sheet, context) {
    sheet.load('tabId,name');

    const usedRange = sheet.getUsedRangeOrNullObject(true);
    usedRange.load('rowCount,columnCount,isNullObject,rowIndex,columnIndex');

    const frozenRange = sheet.freezePanes.getLocationOrNullObject();
    frozenRange.load('rowCount,columnCount,isNullObject');
    await context.sync();

    let frozenRows = 0;
    let frozenCols = 0;
    if (!frozenRange.isNullObject) {
      frozenRows = frozenRange.rowCount;
      frozenCols = frozenRange.columnCount;
    }

    if (usedRange.isNullObject) {
      return {
        sheetId: sheet.tabId,
        name: sheet.name,
        rows: 0,
        cols: 0,
        frozenRows,
        frozenCols,
      };
    }

    return {
      sheetId: sheet.tabId,
      name: sheet.name,
      rows: usedRange.rowIndex + usedRange.rowCount,
      cols: usedRange.columnIndex + usedRange.columnCount,
      frozenRows,
      frozenCols,
    };
  }

  /**
   * Parse a range reference (no sheet prefix) into 1-based inclusive bounds.
   * Handles cell ranges ("A1:C10"), single cells ("B7"), whole-column ranges
   * ("A:Z" / "C:C") and whole-row ranges ("3:3" / "1:5"). Whole-column ranges
   * are clamped to maxRows and whole-row ranges to maxCols so they resolve to a
   * concrete block of the used grid.
   * @param {string} range - A1 range reference
   * @param {number} [maxRows=MAX_EXCEL_ROWS] - Row clamp for whole-column refs
   * @param {number} [maxCols=16384] - Column clamp for whole-row refs
   * @returns {{ startRow: number, endRow: number, startColumn: number, endColumn: number }}
   */
  _parseAddress(range, maxRows = MAX_EXCEL_ROWS, maxCols = 16384) {
    const clean = String(range || '').replace(/\$/g, '').trim().toUpperCase();
    if (!clean) throw new Error('Invalid range: empty');

    const [startPart, endPartRaw] = clean.split(':');
    const endPart = endPartRaw === undefined ? startPart : endPartRaw;

    const startCol = startPart.match(/[A-Z]+/);
    const startRow = startPart.match(/\d+/);
    const endCol = endPart.match(/[A-Z]+/);
    const endRow = endPart.match(/\d+/);

    // Whole-column range ("A:Z" or "C:C"): no row numbers present.
    if (startCol && !startRow && endCol && !endRow) {
      const sc = letterToColumn(startCol[0]);
      const ec = letterToColumn(endCol[0]);
      if (!sc || !ec) throw new Error(`Invalid column range: ${range}`);
      return {
        startRow: 1,
        endRow: Math.max(maxRows, 1),
        startColumn: Math.min(sc, ec),
        endColumn: Math.max(sc, ec),
      };
    }

    // Whole-row range ("3:3" or "1:5"): no column letters present.
    if (!startCol && startRow && !endCol && endRow) {
      const sr = parseInt(startRow[0], 10);
      const er = parseInt(endRow[0], 10);
      return {
        startRow: Math.min(sr, er),
        endRow: Math.max(sr, er),
        startColumn: 1,
        endColumn: Math.max(maxCols, 1),
      };
    }

    // Standard cell range or single cell.
    if (!startCol || !startRow || !endCol || !endRow) {
      throw new Error(`Invalid range: ${range}`);
    }
    const sc = letterToColumn(startCol[0]);
    const ec = letterToColumn(endCol[0]);
    if (!sc || !ec) throw new Error(`Invalid range: ${range}`);
    const sr = parseInt(startRow[0], 10);
    const er = parseInt(endRow[0], 10);
    return {
      startRow: Math.min(sr, er),
      endRow: Math.max(sr, er),
      startColumn: Math.min(sc, ec),
      endColumn: Math.max(ec, sc),
    };
  }

  /**
   * Apply cell-level styles (font, fill, alignment, number format) to a range.
   * Mirrors the reverse-engineered applyCellStyles.
   * @param {Excel.Range} range - Target range
   * @param {Object} styles - cellStyles object from the tool input
   */
  _applyCellStyles(range, styles) {
    if (!styles) return;
    const font = range.format.font;

    if (styles.fontColor) font.color = styles.fontColor;
    if (styles.fontSize) font.size = styles.fontSize;
    if (styles.fontFamily) font.name = styles.fontFamily;
    if (styles.fontWeight) font.bold = styles.fontWeight === 'bold';
    if (styles.fontStyle) font.italic = styles.fontStyle === 'italic';

    if (styles.fontLine) {
      const underline = Excel.RangeUnderlineStyle || {};
      font.underline = styles.fontLine === 'underline'
        ? (underline.single != null ? underline.single : 'Single')
        : (underline.none != null ? underline.none : 'None');
      font.strikethrough = styles.fontLine === 'line-through';
    }

    if (styles.backgroundColor) {
      range.format.fill.color = styles.backgroundColor;
    }
    if (styles.horizontalAlignment) {
      range.format.horizontalAlignment = styles.horizontalAlignment;
    }
    if (styles.numberFormat) {
      range.numberFormat = [[styles.numberFormat]];
    }
  }

  /**
   * Apply border styles to a range's edges. Mirrors the reverse-engineered
   * applyBorderStyles / applyBorderProps.
   * @param {Excel.Range} range - Target range
   * @param {Object} borders - borderStyles object keyed by side (top/bottom/left/right)
   */
  _applyBorderStyles(range, borders) {
    if (!borders) return;
    const borderIndex = Excel.BorderIndex || {};
    const lineStyle = Excel.BorderLineStyle || {};
    const weightEnum = Excel.BorderWeight || {};

    const indexMap = {
      top: borderIndex.edgeTop != null ? borderIndex.edgeTop : 'EdgeTop',
      bottom: borderIndex.edgeBottom != null ? borderIndex.edgeBottom : 'EdgeBottom',
      left: borderIndex.edgeLeft != null ? borderIndex.edgeLeft : 'EdgeLeft',
      right: borderIndex.edgeRight != null ? borderIndex.edgeRight : 'EdgeRight',
    };
    const styleMap = {
      solid: lineStyle.continuous != null ? lineStyle.continuous : 'Continuous',
      dashed: lineStyle.dash != null ? lineStyle.dash : 'Dash',
      dotted: lineStyle.dot != null ? lineStyle.dot : 'Dot',
      double: lineStyle.double != null ? lineStyle.double : 'Double',
    };
    const weightMap = {
      thin: weightEnum.thin != null ? weightEnum.thin : 'Thin',
      medium: weightEnum.medium != null ? weightEnum.medium : 'Medium',
      thick: weightEnum.thick != null ? weightEnum.thick : 'Thick',
    };

    for (const side of Object.keys(borders)) {
      const def = borders[side];
      if (!def) continue;
      const idx = indexMap[side];
      if (idx == null) continue;

      const border = range.format.borders.getItem(idx);
      if (def.style && styleMap[def.style] != null) border.style = styleMap[def.style];
      if (def.weight && weightMap[def.weight] != null) border.weight = weightMap[def.weight];
      if (def.color) border.color = def.color;
    }
  }

  /**
   * Convert an Excel displayed-cell-properties format object into the simplified
   * cellStyles / borderStyles shape used by getCellRanges. Mirrors the
   * reverse-engineered extractDisplayedStyles.
   * @param {Object} format - format object from getDisplayedCellProperties
   * @returns {{ cellStyles?: Object, borderStyles?: Object }}
   */
  _extractDisplayedStyles(format) {
    if (!format) return {};
    const cellStyles = {};

    if (format.font?.color) cellStyles.fontColor = format.font.color;
    if (format.font?.size) cellStyles.fontSize = format.font.size;
    if (format.font?.name) cellStyles.fontFamily = format.font.name;
    if (format.font?.bold) cellStyles.fontWeight = 'bold';
    if (format.font?.italic) cellStyles.fontStyle = 'italic';

    const noneUnderline = (Excel.RangeUnderlineStyle && Excel.RangeUnderlineStyle.none) || 'None';
    if (format.font?.strikethrough) {
      cellStyles.fontLine = 'line-through';
    } else if (format.font?.underline && format.font.underline !== noneUnderline) {
      cellStyles.fontLine = 'underline';
    }

    if (format.fill?.color) cellStyles.backgroundColor = format.fill.color;
    if (format.horizontalAlignment) cellStyles.horizontalAlignment = format.horizontalAlignment;

    const sides = {
      top: format.borders?.top,
      bottom: format.borders?.bottom,
      left: format.borders?.left,
      right: format.borders?.right,
    };
    const borderStyles = {};
    let hasBorders = false;
    for (const side of Object.keys(sides)) {
      const b = sides[side];
      if (b && b.style && b.style !== 'None') {
        borderStyles[side] = { style: b.style, weight: b.weight, color: b.color };
        hasBorders = true;
      }
    }

    return {
      cellStyles: Object.keys(cellStyles).length > 0 ? cellStyles : undefined,
      borderStyles: hasBorders ? borderStyles : undefined,
    };
  }

  /**
   * Load every threaded comment on a worksheet using supported Office.js APIs.
   * Locations are resolved as Range objects so sheet names never need quoting.
   */
  async _loadCommentThreadsByCell(sheet, context) {
    const comments = sheet?.comments;
    if (!comments || typeof comments.load !== 'function') {
      throw new Error('Excel threaded comments are unavailable on this host');
    }

    comments.load('items');
    await context.sync();

    const pending = [];
    for (const comment of comments.items || []) {
      comment.load('content');
      const location = comment.getLocation();
      location.load('rowIndex,columnIndex');
      comment.replies.load('items/content');
      pending.push({ comment, location });
    }
    if (pending.length > 0) await context.sync();

    const threads = new Map();
    for (const { comment, location } of pending) {
      const a1 = `${columnToLetter(location.columnIndex + 1)}${location.rowIndex + 1}`;
      threads.set(a1, {
        comment,
        rootContent: normalizeCommentContent(comment.content),
        replyContents: (comment.replies.items || [])
          .map((reply) => normalizeCommentContent(reply.content))
          .filter(Boolean),
      });
    }
    return threads;
  }

  /**
   * Read a block of rows from a worksheet into a 2-D grid of cell descriptors.
   * Shared by getCellRanges, getRangeAsCsv, and the overwrite/read-back checks
   * inside setCellRange. Mirrors the reverse-engineered getSheet command.
   * @param {Excel.Worksheet} sheet - Source worksheet
   * @param {Excel.RequestContext} context - The active request context
   * @param {Object} opts - { startRow, endRow, numColumns, includeStyles, includeNotes }
   * @returns {Promise<{ rowIndex: number, columnIndex: number, cells: Array<Array<Object>> }>}
   */
  async _readSheetRows(sheet, context, { startRow, endRow, numColumns, includeStyles = false, includeNotes = false }) {
    const safeEndRow = Math.min(endRow, MAX_EXCEL_ROWS);
    const readRows = Math.max(0, Math.min(safeEndRow - startRow + 1, ROW_CHUNK_SIZE));
    const cols = Math.max(1, numColumns || 1);

    if (readRows === 0) {
      return { rowIndex: startRow - 1, columnIndex: 0, cells: [] };
    }

    const range = sheet.getRangeByIndexes(startRow - 1, 0, readRows, cols);
    range.load('values,formulas,rowIndex,columnIndex');

    let displayedProps;
    if (includeStyles) {
      displayedProps = range.getDisplayedCellProperties({
        format: {
          font: { bold: true, color: true, italic: true, name: true, size: true, strikethrough: true, underline: true },
          borders: { color: true, style: true, weight: true },
          fill: { color: true },
          horizontalAlignment: true,
        },
      });
    }

    await context.sync();

    const commentThreads = includeNotes
      ? await this._loadCommentThreadsByCell(sheet, context)
      : new Map();

    const cells = [];
    for (let r = 0; r < range.values.length; r++) {
      const row = [];
      for (let c = 0; c < range.values[r].length; c++) {
        const colNum = range.columnIndex + c + 1;
        const a1 = `${columnToLetter(colNum)}${range.rowIndex + r + 1}`;
        const value = range.values[r][c];
        const formula = range.formulas[r][c];

        const cell = {
          a1,
          value: value !== '' ? value : undefined,
          formula: formula !== '' ? formula : undefined,
        };
        const note = formatCommentThread(commentThreads.get(a1));
        if (note) cell.note = note;

        if (includeStyles && displayedProps) {
          const cellProps = displayedProps.value?.[r]?.[c];
          if (cellProps?.format) {
            const extracted = this._extractDisplayedStyles(cellProps.format);
            if (extracted.cellStyles) cell.cellStyles = extracted.cellStyles;
            if (extracted.borderStyles) cell.borderStyles = extracted.borderStyles;
          }
        }

        row.push(cell);
      }
      cells.push(row);
    }

    return { rowIndex: range.rowIndex, columnIndex: range.columnIndex, cells };
  }

  /**
   * Read an arbitrary row span without losing rows at the host chunk boundary.
   * Callers that require complete safety checks use this helper instead of a
   * single `_readSheetRows` call, which intentionally reads at most 1,000 rows.
   */
  async _readSheetRowsPaged(sheet, context, options) {
    const cells = [];
    let firstRowIndex = Math.max(0, options.startRow - 1);
    let columnIndex = 0;

    for (let chunkStart = options.startRow; chunkStart <= options.endRow; chunkStart += ROW_CHUNK_SIZE) {
      const chunkEnd = Math.min(options.endRow, chunkStart + ROW_CHUNK_SIZE - 1);
      const chunk = await this._readSheetRows(sheet, context, {
        ...options,
        startRow: chunkStart,
        endRow: chunkEnd,
      });
      if (cells.length === 0) {
        firstRowIndex = chunk.rowIndex;
        columnIndex = chunk.columnIndex;
      }
      cells.push(...chunk.cells);
    }

    return { rowIndex: firstRowIndex, columnIndex, cells };
  }

  /**
   * Decide whether a cell descriptor counts as empty. Formatting-only cells
   * count as empty (only value/formula matter), matching the reference.
   * @param {Object} cell - Cell descriptor from _readSheetRows
   * @returns {boolean}
   */
  _isCellEmpty(cell) {
    if (!cell) return true;
    const noValue = cell.value === undefined || cell.value === null || cell.value === '';
    const noFormula = !cell.formula || cell.formula === '';
    return noValue && noFormula;
  }

  /**
   * Resolve the range a copy operation will actually write and find populated
   * cells it would overwrite. Excel expands a destination that is smaller than
   * the source, so checking only the requested destination can miss writes.
   * Cells that map back to themselves are excluded because fill operations
   * commonly include their seed cells in the destination.
   */
  async _findCopyConflicts(sheet, context, sourceRange, destinationRange, scanConflicts = true) {
    const source = sheet.getRange(sourceRange);
    const requestedDestination = sheet.getRange(destinationRange);
    source.load('rowIndex,columnIndex,rowCount,columnCount');
    requestedDestination.load('rowIndex,columnIndex,rowCount,columnCount');
    await context.sync();

    const effectiveRowCount = Math.max(source.rowCount, requestedDestination.rowCount);
    const effectiveColumnCount = Math.max(source.columnCount, requestedDestination.columnCount);
    const effectiveCellCount = effectiveRowCount * effectiveColumnCount;
    const effectiveEndRow = requestedDestination.rowIndex + effectiveRowCount;
    const effectiveEndColumn = requestedDestination.columnIndex + effectiveColumnCount;
    if (
      !Number.isSafeInteger(effectiveCellCount) ||
      effectiveCellCount < 1 ||
      effectiveCellCount > MAX_COPY_OPERATION_CELLS ||
      effectiveEndRow > MAX_EXCEL_ROWS ||
      effectiveEndColumn > 16_384
    ) {
      throw new Error(
        `Copy destination is too large. A single copy is limited to ${MAX_COPY_OPERATION_CELLS.toLocaleString()} cells.`
      );
    }
    const startAddress = `${columnToLetter(requestedDestination.columnIndex + 1)}${requestedDestination.rowIndex + 1}`;
    const endAddress = `${columnToLetter(requestedDestination.columnIndex + effectiveColumnCount)}${requestedDestination.rowIndex + effectiveRowCount}`;
    const effectiveDestinationRange = startAddress === endAddress
      ? startAddress
      : `${startAddress}:${endAddress}`;

    if (!scanConflicts) {
      return { conflicts: [], effectiveDestinationRange };
    }

    const sourceEndRow = source.rowIndex + source.rowCount;
    const sourceEndColumn = source.columnIndex + source.columnCount;
    const conflicts = [];

    const columnsPerPage = Math.min(effectiveColumnCount, COPY_READ_PAGE_CELLS);
    for (let columnOffset = 0; columnOffset < effectiveColumnCount; columnOffset += columnsPerPage) {
      const pageColumnCount = Math.min(columnsPerPage, effectiveColumnCount - columnOffset);
      const rowsPerPage = Math.max(
        1,
        Math.min(ROW_CHUNK_SIZE, Math.floor(COPY_READ_PAGE_CELLS / pageColumnCount))
      );
      for (let rowOffset = 0; rowOffset < effectiveRowCount; rowOffset += rowsPerPage) {
        const pageRowCount = Math.min(rowsPerPage, effectiveRowCount - rowOffset);
        const destinationPage = sheet.getRangeByIndexes(
          requestedDestination.rowIndex + rowOffset,
          requestedDestination.columnIndex + columnOffset,
          pageRowCount,
          pageColumnCount
        );
        destinationPage.load('values,formulas,rowIndex,columnIndex');
        await context.sync();

        for (let r = 0; r < destinationPage.values.length; r++) {
          for (let c = 0; c < destinationPage.values[r].length; c++) {
            const absoluteRow = destinationPage.rowIndex + r;
            const absoluteColumn = destinationPage.columnIndex + c;
            const relativeRow = rowOffset + r;
            const relativeColumn = columnOffset + c;
            const mappedSourceRow = source.rowIndex + (relativeRow % source.rowCount);
            const mappedSourceColumn = source.columnIndex + (relativeColumn % source.columnCount);
            const isIdentityCopy = absoluteRow === mappedSourceRow &&
              absoluteColumn === mappedSourceColumn &&
              absoluteRow >= source.rowIndex && absoluteRow < sourceEndRow &&
              absoluteColumn >= source.columnIndex && absoluteColumn < sourceEndColumn;
            if (isIdentityCopy) continue;

            const value = destinationPage.values[r][c];
            const formula = destinationPage.formulas[r][c];
            const populated = (value !== '' && value != null) || (formula !== '' && formula != null);
            if (populated) conflicts.push(`${columnToLetter(absoluteColumn + 1)}${absoluteRow + 1}`);
          }
        }
      }
    }
    return { conflicts, effectiveDestinationRange };
  }

  /**
   * Screen the live formulas that a copy operation will duplicate. Structured
   * copy tools can otherwise bypass the formula checks applied to direct
   * writes. Addresses replaced by the current setCellRange call are excluded
   * because their resolved replacement values were already screened.
   */
  async _assertCopySourceFormulasSafe(
    sheet,
    context,
    sourceRange,
    ignoredAddresses = new Set()
  ) {
    const source = sheet.getRange(sourceRange);
    source.load('formulas,rowIndex,columnIndex,rowCount,columnCount');
    await context.sync();

    const cellCount = source.rowCount * source.columnCount;
    if (
      !Number.isSafeInteger(cellCount) ||
      cellCount < 1 ||
      cellCount > MAX_COPY_OPERATION_CELLS
    ) {
      throw new Error(
        `Copy source is too large. A single copy is limited to ${MAX_COPY_OPERATION_CELLS.toLocaleString()} cells.`
      );
    }
    if (
      !Array.isArray(source.formulas) ||
      source.formulas.length !== source.rowCount ||
      source.formulas.some(
        (row) => !Array.isArray(row) || row.length !== source.columnCount
      )
    ) {
      throw new Error('Copy source formula inspection failed before mutation');
    }

    const ignored = new Set(
      [...ignoredAddresses].map((address) => String(address || '').toUpperCase())
    );
    const formulas = [];
    for (let row = 0; row < source.rowCount; row++) {
      for (let column = 0; column < source.columnCount; column++) {
        const address =
          `${columnToLetter(source.columnIndex + column + 1)}${source.rowIndex + row + 1}`;
        if (!ignored.has(address)) formulas.push(source.formulas[row][column]);
      }
    }
    assertFormulasAllowed(formulas);
  }

  /**
   * Recalculate only ranges affected by a structured write. If range-level
   * calculation is unavailable, readback proceeds without expanding the trust
   * boundary to a full-workbook calculation.
   */
  async _calculateRangesBestEffort(sheet, context, rangeAddresses) {
    const uniqueAddresses = [
      ...new Set(rangeAddresses.filter(Boolean).map((address) => String(address))),
    ];
    const ranges = uniqueAddresses.map((address) => sheet.getRange(address));
    if (ranges.length === 0 || ranges.some((range) => typeof range?.calculate !== 'function')) {
      return;
    }
    try {
      for (const range of ranges) range.calculate();
      await context.sync();
    } catch (recalcErr) {
      // Some hosts do not support range-level calculation. Never widen to a
      // workbook calculation because unrelated formulas were not screened.
    }
  }

  _throwOverwriteConflicts(conflicts) {
    const preview = conflicts.slice(0, 10).join(', ');
    const extra = conflicts.length - 10;
    const suffix = extra > 0 ? `${preview}, ... (and ${extra} more)` : preview;
    throw new Error(
      `Would overwrite ${conflicts.length} non-empty cell${conflicts.length > 1 ? 's' : ''}: ${suffix}. ` +
      'To proceed with overwriting existing data, retry with allow_overwrite set to true.'
    );
  }

  async _readFormulaResults(sheet, context, rangeAddress, limit = DEFAULT_CELL_LIMIT) {
    const range = sheet.getRange(rangeAddress);
    range.load('rowIndex,columnIndex,rowCount,columnCount');
    await context.sync();

    const cellCount = range.rowCount * range.columnCount;
    if (
      !Number.isSafeInteger(cellCount) ||
      cellCount < 1 ||
      cellCount > MAX_COPY_OPERATION_CELLS
    ) {
      throw new Error(
        `Formula readback is too large. A single copy is limited to ${MAX_COPY_OPERATION_CELLS.toLocaleString()} cells.`
      );
    }

    const results = {};
    let formulaCount = 0;
    let returnedCount = 0;
    const columnsPerPage = Math.min(range.columnCount, COPY_READ_PAGE_CELLS);
    for (let columnOffset = 0; columnOffset < range.columnCount; columnOffset += columnsPerPage) {
      const pageColumnCount = Math.min(columnsPerPage, range.columnCount - columnOffset);
      const rowsPerPage = Math.max(
        1,
        Math.min(ROW_CHUNK_SIZE, Math.floor(COPY_READ_PAGE_CELLS / pageColumnCount))
      );
      for (let rowOffset = 0; rowOffset < range.rowCount; rowOffset += rowsPerPage) {
        const pageRowCount = Math.min(rowsPerPage, range.rowCount - rowOffset);
        const page = sheet.getRangeByIndexes(
          range.rowIndex + rowOffset,
          range.columnIndex + columnOffset,
          pageRowCount,
          pageColumnCount
        );
        page.load('values,formulas,rowIndex,columnIndex');
        await context.sync();
        for (let r = 0; r < page.formulas.length; r++) {
          for (let c = 0; c < page.formulas[r].length; c++) {
            const formula = page.formulas[r][c];
            if (typeof formula !== 'string' || !formula.startsWith('=')) continue;
            formulaCount += 1;
            if (returnedCount >= limit) continue;
            const a1 = `${columnToLetter(page.columnIndex + c + 1)}${page.rowIndex + r + 1}`;
            const value = page.values[r][c];
            results[a1] = typeof value === 'number' || (typeof value === 'string' && value.startsWith('#'))
              ? value
              : String(value ?? '');
            returnedCount += 1;
          }
        }
      }
    }
    return {
      results,
      truncated: formulaCount > returnedCount,
    };
  }

  // ===========================================================================
  // CORE: Execute arbitrary Office.js code
  // ===========================================================================

  /**
   * Execute arbitrary Office.js code inside Excel.run().
   * Uses SES Compartment sandbox for security, exactly like
   * PowerPointBridge.executeCode but with the Excel host namespace.
   *
   * @param {string} code - JavaScript code string to execute inside Excel.run()
   * @returns {Promise<any>} - Result of the code execution
   */
  async executeCode(code) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }

    // Escape-hatch formula screen: reject AI code that writes a blocked formula
    // (=WEBSERVICE / =IMPORTDATA / DDE etc.). set_cell_range is screened too;
    // this covers the raw Office.js path. Matches the reference's dual enforcement.
    assertCodeFreeOfBlockedFormulas(code);

    return this._safeOfficeRun(async (context) => {
      let result;
      try {
        // Wrap the context so any formula assigned to a range's
        // formulas / formulasLocal is screened at the write boundary. This
        // catches formulas the static code scan above cannot see because they
        // are assembled from string fragments at run time. context.sync() is
        // still driven through this wrapper, so the screened writes are what
        // reach Office.js.
        const screenedContext = screenFormulaWrites(context);

        // Primary path: SES Compartment sandbox
        result = await sandboxedEval(code, {
          context: screenedContext,
          Excel,
        });
      } catch (sesError) {
        if (isSesInitializationError(sesError)) {
          throw createSesUnavailableError('execute_office_js');
        }
        throw sesError;
      }
      await context.sync();
      return result;
    }, { timeoutMs: TOOL_TIMEOUT_MS, label: 'execute_office_js' });
  }

  // ===========================================================================
  // TOOL: get_cell_ranges
  // ===========================================================================

  /**
   * Read values, formulas, and (optionally) styles for one or more A1 ranges.
   * Respects a non-empty-cell budget (`cellLimit`), truncating at row
   * boundaries and returning the leftover ranges in `nextRanges` so the model
   * can page through large reads.
   *
   * @param {Object} params
   * @param {number} params.sheetId - tabId of the sheet to read
   * @param {string[]} params.ranges - A1 ranges (e.g. ["A1:C10", "E1:F5"])
   * @param {boolean} [params.includeStyles=true] - Include cell/border styles
   * @param {boolean} [params.includeNotes=false] - Include threaded source comments for populated cells
   * @param {number} [params.cellLimit=2000] - Max non-empty cells to return
   * @returns {Promise<Object>} - { success, hasMore, nextRanges?, worksheet }
   */
  async getCellRanges({ sheetId, ranges, includeStyles = true, includeNotes = false, cellLimit = DEFAULT_CELL_LIMIT }) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }
    if (!Array.isArray(ranges) || ranges.length === 0) {
      throw new Error('ranges must be a non-empty array of A1 notation strings');
    }

    return this._safeOfficeRun(async (context) => {
      const sheet = await this._getWorksheetById(context, sheetId);
      const meta = await this._getSheetMetadata(sheet, context);

      const limit = Math.max(1, Math.min(cellLimit ?? DEFAULT_CELL_LIMIT, DEFAULT_CELL_LIMIT));
      const worksheet = {
        sheetId: meta.sheetId,
        name: meta.name,
        dimension: { rows: meta.rows, cols: meta.cols },
        cells: {},
      };

      if (meta.rows === 0 || meta.cols === 0) {
        return { success: true, hasMore: false, worksheet };
      }

      let cellCount = 0;
      const remaining = [];
      let truncated = false;

      for (let i = 0; i < ranges.length; i++) {
        const rangeStr = ranges[i];

        if (truncated) {
          remaining.push(rangeStr);
          continue;
        }

        const { startRow, endRow, startColumn, endColumn } =
          this._parseAddress(rangeStr, meta.rows, meta.cols);

        // Clamp the range to the used grid so we never read past the data.
        const effEndRow = Math.min(endRow, meta.rows);
        const effEndCol = Math.min(endColumn, meta.cols);
        if (startRow > effEndRow || startColumn > effEndCol) {
          continue;
        }

        let stoppedAtRow = null;
        for (
          let chunkStart = startRow;
          chunkStart <= effEndRow && !truncated;
          chunkStart += ROW_CHUNK_SIZE
        ) {
          const chunkEnd = Math.min(effEndRow, chunkStart + ROW_CHUNK_SIZE - 1);
          const { cells } = await this._readSheetRows(sheet, context, {
            startRow: chunkStart,
            endRow: chunkEnd,
            numColumns: effEndCol,
            includeStyles,
            includeNotes,
          });

          for (let r = 0; r < cells.length && !truncated; r++) {
            const absRow = chunkStart + r;
            const rowCells = cells[r].slice(startColumn - 1, effEndCol);
            const rowNonEmpty = rowCells.reduce(
              (count, cell) => count + (this._isCellEmpty(cell) ? 0 : 1),
              0
            );

            // Never emit a partial row. A single oversized first row is kept
            // intact; otherwise the next range starts at this exact row.
            if (rowNonEmpty > 0 && cellCount > 0 && cellCount + rowNonEmpty > limit) {
              truncated = true;
              stoppedAtRow = absRow;
              break;
            }

            for (const cell of rowCells) {
              worksheet.cells[cell.a1] = this._formatCellOut(cell, includeStyles);
            }
            cellCount += rowNonEmpty;
          }
        }

        if (truncated && stoppedAtRow != null && stoppedAtRow <= effEndRow) {
          remaining.push(
            `${columnToLetter(startColumn)}${stoppedAtRow}:${columnToLetter(effEndCol)}${effEndRow}`
          );
        }
      }

      return {
        success: true,
        hasMore: remaining.length > 0,
        nextRanges: remaining.length > 0 ? remaining : undefined,
        worksheet,
      };
    }, { label: 'get_cell_ranges' });
  }

  /**
   * Reduce a cell descriptor to the JSON-serializable shape returned to the
   * model, dropping undefined fields and (optionally) styles.
   * @param {Object} cell - Cell descriptor from _readSheetRows
   * @param {boolean} includeStyles - Whether to keep cellStyles/borderStyles
   * @returns {Object}
   */
  _formatCellOut(cell, includeStyles) {
    const out = {};
    if (cell.value !== undefined) out.value = cell.value;
    if (cell.formula !== undefined) out.formula = cell.formula;
    if (cell.note !== undefined) out.note = cell.note;
    if (includeStyles) {
      if (cell.cellStyles) out.cellStyles = cell.cellStyles;
      if (cell.borderStyles) out.borderStyles = cell.borderStyles;
    }
    return out;
  }

  // ===========================================================================
  // TOOL: get_range_as_csv
  // ===========================================================================

  /**
   * Read a single A1 range and return it as a CSV string ready for analysis.
   * Respects `maxRows` (paged via repeated calls) and quotes values that
   * contain commas, quotes, or newlines.
   *
   * @param {Object} params
   * @param {number} params.sheetId - tabId of the sheet to read
   * @param {string} params.range - A1 range to export (e.g. "A1:Z100", "A:Z")
   * @param {boolean} [params.includeHeaders=true] - First row is a header row
   * @param {number} [params.maxRows=500] - Max data rows to return
   * @returns {Promise<Object>} - { success, csv, rowCount, columnCount, hasMore, sheetName }
   */
  async getRangeAsCsv({ sheetId, range, includeHeaders = true, maxRows = DEFAULT_CSV_MAX_ROWS }) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }
    if (!range) {
      throw new Error('range is required');
    }

    return this._safeOfficeRun(async (context) => {
      const sheet = await this._getWorksheetById(context, sheetId);
      const meta = await this._getSheetMetadata(sheet, context);

      if (meta.rows === 0 || meta.cols === 0) {
        return { success: true, csv: '', rowCount: 0, columnCount: 0, hasMore: false, sheetName: meta.name };
      }

      const parsed = this._parseAddress(range, meta.rows, meta.cols);
      const startRow = parsed.startRow;
      const startColumn = parsed.startColumn;
      const rawEndRow = Math.min(parsed.endRow, meta.rows);
      const endColumn = Math.min(parsed.endColumn, meta.cols);

      // A header row consumes one extra physical row on top of the data budget.
      const rowBudget = includeHeaders ? (maxRows ?? DEFAULT_CSV_MAX_ROWS) + 1 : (maxRows ?? DEFAULT_CSV_MAX_ROWS);
      const endRow = Math.min(rawEndRow, startRow + rowBudget - 1);
      const hasMore = rawEndRow > endRow;

      const numColumns = endColumn - startColumn + 1;
      const { cells } = await this._readSheetRows(sheet, context, {
        startRow,
        endRow,
        numColumns: endColumn,
        includeStyles: false,
      });

      const rows = [];
      for (let i = 0; i < cells.length; i++) {
        const rowCells = cells[i].slice(startColumn - 1, endColumn);
        const values = [];
        for (let j = 0; j < numColumns; j++) {
          let val = rowCells[j]?.value;
          if (val === undefined || val === null) val = '';
          let str = String(val);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            str = `"${str.replace(/"/g, '""')}"`;
          }
          values.push(str);
        }
        rows.push(values.join(','));
      }

      const dataRowCount = includeHeaders ? Math.max(0, rows.length - 1) : rows.length;

      return {
        success: true,
        csv: rows.join('\n'),
        rowCount: dataRowCount,
        columnCount: numColumns,
        hasMore,
        sheetName: meta.name,
      };
    }, { label: 'get_range_as_csv' });
  }

  // ===========================================================================
  // TOOL: search_data
  // ===========================================================================

  /**
   * Search for text across the workbook (or a single sheet) and return the
   * matching cell locations.
   *
   * The plain case (literal substring/whole-cell match against displayed
   * values) uses Excel's native findAllOrNullObject, then pages the flat
   * result list with `offset` and `options.maxResults`. Whenever a richer
   * matching mode is requested - `useRegex`, `matchFormulas`, or
   * `ignoreDiacritics` - native search cannot express the semantics, so the
   * call falls through to `_searchDataManual`, a per-cell chunked scan of the
   * used range that mirrors the reverse-engineered search_data fallback.
   *
   * @param {Object} params
   * @param {string} params.searchTerm - Text (or regex source) to search for
   * @param {number} [params.sheetId] - Limit search to one sheet (tabId)
   * @param {string} [params.range] - Optional A1 range to filter matches to
   * @param {number} [params.offset=0] - Number of results to skip (pagination)
   * @param {Object} [params.options] - { matchCase, matchEntireCell, maxResults, useRegex, matchFormulas, ignoreDiacritics }
   * @returns {Promise<Object>} - { success, matches, totalFound, returned, offset, hasMore, nextOffset, ... }
   */
  async searchData({ searchTerm, sheetId, range, offset = 0, options = {} }) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }
    if (typeof searchTerm !== 'string' || searchTerm.length === 0) {
      throw new Error('searchTerm is required');
    }

    const opts = options || {};
    const maxResults = opts.maxResults || DEFAULT_SEARCH_MAX_RESULTS;
    const skip = offset || 0;

    // Anything native findAll cannot express (regex, formula-text matching, or
    // diacritic folding) routes to the manual chunked scan instead.
    if (opts.useRegex || opts.matchFormulas || opts.ignoreDiacritics) {
      return this._searchDataManual({ searchTerm, sheetId, range, skip, maxResults, opts });
    }

    return this._safeOfficeRun(async (context) => {
      const worksheets = context.workbook.worksheets;
      worksheets.load('items/name,items/tabId');
      await context.sync();

      const targetId = sheetId != null ? Number(sheetId) : null;
      const sheetsToSearch = targetId != null
        ? worksheets.items.filter((ws) => ws.tabId === targetId)
        : worksheets.items;

      // Optional A1 scope filter (applied to native results after the fact).
      let scope = null;
      if (range) {
        const p = this._parseAddress(range);
        scope = p;
      }

      const matches = [];
      for (const sheet of sheetsToSearch) {
        const found = sheet.findAllOrNullObject(searchTerm, {
          matchCase: opts.matchCase || false,
          completeMatch: opts.matchEntireCell || false,
        });
        found.load('isNullObject');
        await context.sync();

        if (found.isNullObject) continue;

        const areas = found.areas;
        areas.load('items/values,items/formulas,items/rowIndex,items/columnIndex,items/rowCount,items/columnCount');
        await context.sync();

        for (const area of areas.items) {
          for (let r = 0; r < area.rowCount; r++) {
            for (let c = 0; c < area.columnCount; c++) {
              const row = area.rowIndex + r + 1;
              const col = area.columnIndex + c + 1;

              if (scope && (row < scope.startRow || row > scope.endRow ||
                col < scope.startColumn || col > scope.endColumn)) {
                continue;
              }

              const value = area.values[r][c];
              const formula = area.formulas[r][c];
              matches.push({
                sheetName: sheet.name,
                sheetId: sheet.tabId,
                a1: `${columnToLetter(col)}${row}`,
                value: value !== '' ? value : undefined,
                formula: formula !== '' && formula !== value ? formula : null,
                row,
                column: col,
              });
            }
          }
        }
      }

      const page = matches.slice(skip, skip + maxResults);
      const hasMore = matches.length > skip + maxResults;

      return {
        success: true,
        matches: page,
        totalFound: matches.length,
        returned: page.length,
        offset: skip,
        hasMore,
        searchTerm,
        searchScope: targetId != null ? `Sheet ${targetId}` : 'All sheets',
        nextOffset: hasMore ? skip + maxResults : null,
      };
    }, { label: 'search_data' });
  }

  /**
   * Strip combining diacritical marks from a string via NFD decomposition.
   * Used to fold accents when `options.ignoreDiacritics` is set so that, e.g.,
   * "resume" matches "resume" with an accented e.
   * @param {string} str - Source string
   * @returns {string} - The accent-folded string
   */
  _stripDiacritics(str) {
    return String(str).normalize('NFD').replace(/\p{Diacritic}/gu, '');
  }

  /**
   * Manual fallback for search_data when native findAll cannot express the
   * requested semantics (`useRegex`, `matchFormulas`, or `ignoreDiacritics`).
   *
   * Iterates the used range of the target sheet(s) in ROW_CHUNK_SIZE-row
   * chunks (reusing `_readSheetRows`), matching each cell against its displayed
   * value or, when `matchFormulas` is set, its formula text. Matching is one of:
   * a `RegExp(searchTerm, matchCase ? 'g' : 'gi')` test (`useRegex`), a strict
   * equals (`matchEntireCell`), or a substring `includes`, with case folding and
   * optional diacritic folding applied to both sides. Honours the optional
   * `sheetId` and `range` scopes, `offset` paging, and `maxResults` exactly like
   * the native path, and returns the identical result shape. A malformed regex
   * is caught and surfaced as `{ success: false, error }`.
   *
   * @param {Object} params
   * @param {string} params.searchTerm - Text or regex source to search for
   * @param {number} [params.sheetId] - Limit search to one sheet (tabId)
   * @param {string} [params.range] - Optional A1 range to filter matches to
   * @param {number} params.skip - Number of results to skip (pagination offset)
   * @param {number} params.maxResults - Page size cap
   * @param {Object} params.opts - { matchCase, matchEntireCell, useRegex, matchFormulas, ignoreDiacritics }
   * @returns {Promise<Object>} - Same shape as searchData's native path
   */
  async _searchDataManual({ searchTerm, sheetId, range, skip, maxResults, opts }) {
    // Compile the regex up front so a bad pattern fails before any sheet I/O.
    let regex = null;
    if (opts.useRegex) {
      try {
        regex = new RegExp(searchTerm, opts.matchCase ? 'g' : 'gi');
      } catch (err) {
        return {
          success: false,
          error: `Invalid regular expression: ${err.message}`,
          searchTerm,
        };
      }
    }

    const ignoreDiacritics = !!opts.ignoreDiacritics;
    const fold = (s) => (ignoreDiacritics ? this._stripDiacritics(s) : s);

    // Pre-fold the needle once for the non-regex literal comparisons.
    const needle = fold(searchTerm);
    const needleLower = needle.toLowerCase();

    /**
     * Decide whether a single cell's target text satisfies the active mode.
     * @param {string} target - Displayed value or formula text for the cell
     * @returns {boolean}
     */
    const cellMatches = (target) => {
      const haystack = fold(target);
      if (regex) {
        // Reset lastIndex: the global flag makes .test() stateful across calls.
        regex.lastIndex = 0;
        return regex.test(haystack);
      }
      if (opts.matchEntireCell) {
        return opts.matchCase
          ? haystack === needle
          : haystack.toLowerCase() === needleLower;
      }
      return opts.matchCase
        ? haystack.includes(needle)
        : haystack.toLowerCase().includes(needleLower);
    };

    return this._safeOfficeRun(async (context) => {
      const worksheets = context.workbook.worksheets;
      worksheets.load('items/name,items/tabId');
      await context.sync();

      const targetId = sheetId != null ? Number(sheetId) : null;
      const sheetsToSearch = targetId != null
        ? worksheets.items.filter((ws) => ws.tabId === targetId)
        : worksheets.items;

      // Optional A1 scope filter, mirroring the native path's after-the-fact
      // row/column clamp.
      const scope = range ? this._parseAddress(range) : null;

      const matches = [];
      const limit = skip + maxResults;

      for (const sheet of sheetsToSearch) {
        if (matches.length >= limit) break;

        const meta = await this._getSheetMetadata(sheet, context);
        if (meta.rows === 0 || meta.cols === 0) continue;

        const numColumns = meta.cols;

        for (let startRow = 1; startRow <= meta.rows && matches.length < limit; startRow += ROW_CHUNK_SIZE) {
          const endRow = Math.min(startRow + ROW_CHUNK_SIZE - 1, meta.rows);
          const { cells } = await this._readSheetRows(sheet, context, {
            startRow,
            endRow,
            numColumns,
          });

          for (let r = 0; r < cells.length && matches.length < limit; r++) {
            const row = startRow + r;
            for (let c = 0; c < cells[r].length; c++) {
              const col = c + 1;

              if (scope && (row < scope.startRow || row > scope.endRow ||
                col < scope.startColumn || col > scope.endColumn)) {
                continue;
              }

              const cell = cells[r][c];
              const text = cell.value != null ? String(cell.value) : '';
              const formula = cell.formula || '';
              const target = opts.matchFormulas ? formula : text;

              if (cellMatches(target)) {
                matches.push({
                  sheetName: sheet.name,
                  sheetId: sheet.tabId,
                  a1: cell.a1,
                  value: cell.value,
                  formula: cell.formula || null,
                  row,
                  column: col,
                });
                if (matches.length >= limit) break;
              }
            }
          }
        }
      }

      const page = matches.slice(skip, skip + maxResults);
      const hasMore = matches.length > skip + maxResults;

      return {
        success: true,
        matches: page,
        totalFound: matches.length,
        returned: page.length,
        offset: skip,
        hasMore,
        searchTerm,
        searchScope: targetId != null ? `Sheet ${targetId}` : 'All sheets',
        nextOffset: hasMore ? skip + maxResults : null,
      };
    }, { label: 'search_data_manual' });
  }

  // ===========================================================================
  // TOOL: get_all_objects
  // ===========================================================================

  /**
   * List all charts and pivot tables in the workbook (or a single sheet),
   * optionally filtered to a single object id.
   *
   * @param {Object} [params]
   * @param {number} [params.sheetId] - Limit to one sheet (tabId)
   * @param {string} [params.id] - Return only the object with this id
   * @returns {Promise<Object>} - { success, objects }
   */
  async getAllObjects({ sheetId, id } = {}) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const worksheets = context.workbook.worksheets;
      worksheets.load('items/name,items/tabId');
      await context.sync();

      const targetId = sheetId != null ? Number(sheetId) : null;
      const sheets = targetId != null
        ? worksheets.items.filter((ws) => ws.tabId === targetId)
        : worksheets.items;

      const objects = [];

      for (const sheet of sheets) {
        const charts = sheet.charts;
        const pivots = sheet.pivotTables;
        charts.load('items/id,items/name,items/chartType,items/top,items/left');
        pivots.load('items/id,items/name');
        await context.sync();

        for (const chart of charts.items) {
          chart.title.load('text');
        }
        await context.sync();

        for (const chart of charts.items) {
          objects.push({
            id: chart.id,
            type: 'chart',
            sheetId: sheet.tabId,
            chartType: chart.chartType,
            title: chart.title.text || chart.name,
            position: { top: chart.top, left: chart.left },
          });
        }

        for (const pivot of pivots.items) {
          objects.push({
            id: pivot.id,
            type: 'pivotTable',
            sheetId: sheet.tabId,
            name: pivot.name,
          });
        }
      }

      const filtered = id ? objects.filter((o) => o.id === id) : objects;
      return { success: true, objects: filtered };
    }, { label: 'get_all_objects' });
  }

  // ===========================================================================
  // TOOL: set_cell_range
  // ===========================================================================

  /**
   * Write values, formulas, notes, and formatting to a range, with overwrite
   * protection and a post-recalc read-back of formula cells.
   *
   * When `allow_overwrite` is falsy, the target range is pre-read and any
   * non-empty cell (formatting-only counts as empty) triggers a structured
   * error listing the conflicting addresses, so the model can confirm and
   * retry with `allow_overwrite: true`.
   *
   * After writing, the written range is re-read so the returned
   * `formula_results` reflect computed values, letting the model self-check
   * for #REF!/#VALUE!/#NAME? errors.
   *
   * @param {Object} params
   * @param {number} params.sheetId - tabId of the sheet to modify
   * @param {string} params.range - A1 range; cells must match its dimensions
   * @param {Array<Array<Object>>} params.cells - 2-D array of cell descriptors
   * @param {string} [params.copyToRange] - Copy the written range to here
   * @param {Object} [params.resizeWidth] - Column width spec applied after write
   * @param {Object} [params.resizeHeight] - Row height spec applied after write
   * @param {boolean} [params.allow_overwrite] - Permit overwriting non-empty cells
   * @returns {Promise<Object>} - { success, formula_results?, ... }
   */
  async setCellRange({ sheetId, range, cells, copyToRange, resizeWidth, resizeHeight, allow_overwrite }) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }
    if (!range) {
      throw new Error('range is required');
    }
    if (!Array.isArray(cells)) {
      throw new Error('cells must be a 2-D array (even a single cell is [[cell]])');
    }

    // Security: reject blocked formulas (=WEBSERVICE / =IMPORTDATA / =RTD / DDE
    // etc.) before touching the sheet, matching the reference's set_cell_range
    // screen. These can exfiltrate sheet data over the network on recalculation.
    // B1: screen formula-shaped VALUES too, not just cell.formula — Excel treats
    // a =/+/-/@-prefixed string written via .values as a live formula, so
    // {value:"=WEBSERVICE(...)"} (and bare-scalar formula strings) must be
    // screened. assertFormulasAllowed ignores non-formula-shaped strings, so
    // ordinary values and legitimate formulas pass through untouched.
    {
      const screened = [];
      for (const row of cells) {
        if (!Array.isArray(row)) continue;
        for (const cell of row) {
          if (cell == null) continue;
          if (typeof cell === 'object') {
            if (cell.formula != null) screened.push(cell.formula);
            if (cell.value != null) screened.push(cell.value);
          } else {
            screened.push(cell); // bare scalar coerced to { value } below
          }
        }
      }
      assertFormulasAllowed(screened);
    }

    return this._safeOfficeRun(async (context) => {
      const sheet = await this._getWorksheetById(context, sheetId);
      const { startRow, endRow, startColumn, endColumn } = this._parseAddress(range);

      // Flatten the 2-D input into a list of (a1, cell) writes, and remember
      // which cells carry a value/formula (for the overwrite + read-back steps).
      const writes = [];
      const dataCells = [];
      const formulaCells = [];

      for (let row = startRow; row <= endRow; row++) {
        for (let col = startColumn; col <= endColumn; col++) {
          const rowIdx = row - startRow;
          const colIdx = col - startColumn;
          // The tool schema accepts each cell as a raw scalar OR an object
          // ({value, formula, note, styles}). Coerce a bare scalar to { value }
          // so the natural form cells:[["text"]] writes on the FIRST attempt,
          // and so falsy-but-real values (0, false, '') are not dropped by a
          // truthiness check. Skip only genuinely absent positions
          // (null/undefined / sparse rows); {} stays a no-op empty cell.
          // (The reverse-engineered reference requires object cells; this is a
          // deliberately more lenient superset, kept because our backend schema
          // permits scalars. Do not "simplify" back to an object-only gate.)
          const raw = cells[rowIdx]?.[colIdx];
          if (raw === undefined || raw === null) continue;
          const cell = (typeof raw === 'object') ? raw : { value: raw };

          const a1 = `${columnToLetter(col)}${row}`;
          writes.push({ a1, cell });

          if (cell.formula !== undefined) formulaCells.push(a1);
          if (cell.value !== undefined || cell.formula !== undefined) dataCells.push(a1);
        }
      }

      // OVERWRITE PROTECTION: pre-read the affected rows and collect addresses
      // of non-empty cells that would be clobbered. Formatting-only cells are
      // treated as empty, so they never block a write.
      if (!allow_overwrite && dataCells.length > 0) {
        let minRow = Infinity;
        let maxRow = 0;
        for (const a1 of dataCells) {
          const { row } = parseA1(a1);
          minRow = Math.min(minRow, row);
          maxRow = Math.max(maxRow, row);
        }

        const existing = await this._readSheetRowsPaged(sheet, context, {
          startRow: minRow,
          endRow: maxRow,
          numColumns: endColumn,
          includeStyles: false,
        });

        const existingMap = new Map();
        for (const r of existing.cells) {
          for (const c of r) {
            if (c.a1) existingMap.set(c.a1, c);
          }
        }

        const conflicts = [];
        for (const a1 of dataCells) {
          const prev = existingMap.get(a1);
          if (prev && !this._isCellEmpty(prev)) conflicts.push(a1);
        }

        if (conflicts.length > 0) {
          this._throwOverwriteConflicts(conflicts);
        }
      }

      let effectiveCopyToRange = copyToRange;
      if (copyToRange) {
        const copyInspection = await this._findCopyConflicts(
          sheet,
          context,
          range,
          copyToRange,
          !allow_overwrite
        );
        effectiveCopyToRange = copyInspection.effectiveDestinationRange;
        if (copyInspection.conflicts.length > 0) {
          this._throwOverwriteConflicts(copyInspection.conflicts);
        }
        await this._assertCopySourceFormulasSafe(
          sheet,
          context,
          range,
          new Set(dataCells)
        );
      }

      const noteWrites = writes
        .filter(({ cell }) => cell.note !== undefined && cell.note !== '')
        .map(({ a1, cell }) => ({ a1, content: normalizeCommentContent(cell.note) }))
        .filter(({ content }) => content.length > 0);
      const commentThreads = noteWrites.length > 0
        ? await this._loadCommentThreadsByCell(sheet, context)
        : new Map();

      // WRITE values/formulas, styles, and notes in one request batch.
      const notesAppended = [];
      for (const { a1, cell } of writes) {
        const cellRange = sheet.getRange(a1);
        if (cell.formula !== undefined) {
          cellRange.formulas = [[cell.formula]];
        } else if (cell.value !== undefined) {
          cellRange.values = [[cell.value]];
        }

        if (cell.cellStyles) this._applyCellStyles(cellRange, cell.cellStyles);
        if (cell.borderStyles) this._applyBorderStyles(cellRange, cell.borderStyles);

        if (cell.note !== undefined && cell.note !== '') {
          const noteContent = normalizeCommentContent(cell.note);
          const existingThread = commentThreads.get(a1);
          if (!existingThread) {
            sheet.comments.add(cellRange, noteContent);
          } else if (!commentThreadHasExactContent(existingThread, noteContent)) {
            existingThread.comment.replies.add(noteContent);
            notesAppended.push(a1);
          }
        }
      }
      await context.sync();

      const notesWritten = [];
      if (noteWrites.length > 0) {
        const verifiedThreads = await this._loadCommentThreadsByCell(sheet, context);
        for (const { a1, content } of noteWrites) {
          if (!commentThreadHasExactContent(verifiedThreads.get(a1), content)) {
            throw new Error(
              `Cell source comment verification failed at ${a1}. The value or formula may already be written; re-read the cell and comment before retrying.`
            );
          }
          notesWritten.push(a1);
        }
      }

      // COPY TO RANGE (optional): copies values, formulas, AND formatting.
      if (copyToRange) {
        sheet.getRange(copyToRange).copyFrom(sheet.getRange(range));
        await context.sync();
      }

      // RESIZE (optional): width/height applied to the whole written range.
      if (resizeWidth || resizeHeight) {
        this._applyResize(sheet.getRange(range), resizeWidth, resizeHeight);
        await context.sync();
      }

      // READ BACK formula cells post-recalc so the model sees computed results
      // and can spot #REF!/#VALUE!/#NAME? errors.
      const formulaResults = {};
      let seedFormulaResultsTruncated = false;
      if (formulaCells.length > 0 || copyToRange) {
        await this._calculateRangesBestEffort(sheet, context, [
          ...formulaCells,
          copyToRange ? effectiveCopyToRange : null,
        ]);
      }

      if (formulaCells.length > 0) {
        let minRow = Infinity;
        let maxRow = 0;
        for (const a1 of formulaCells) {
          const { row } = parseA1(a1);
          minRow = Math.min(minRow, row);
          maxRow = Math.max(maxRow, row);
        }

        const readBack = await this._readSheetRowsPaged(sheet, context, {
          startRow: minRow,
          endRow: maxRow,
          numColumns: endColumn,
          includeStyles: false,
        });

        const cellMap = new Map();
        for (const r of readBack.cells) {
          for (const c of r) {
            if (c.a1) cellMap.set(c.a1, c);
          }
        }
        let returnedFormulaCount = 0;
        for (const a1 of formulaCells) {
          if (returnedFormulaCount >= DEFAULT_CELL_LIMIT) {
            seedFormulaResultsTruncated = true;
            continue;
          }
          const val = cellMap.get(a1)?.value;
          if (typeof val === 'number' || (typeof val === 'string' && val.startsWith('#'))) {
            formulaResults[a1] = val;
          } else {
            formulaResults[a1] = String(val ?? '');
          }
          returnedFormulaCount += 1;
        }
      }

      let copiedFormulaResultsTruncated = false;
      if (copyToRange) {
        const copied = await this._readFormulaResults(sheet, context, effectiveCopyToRange);
        Object.assign(formulaResults, copied.results);
        copiedFormulaResultsTruncated = copied.truncated;
      }

      return {
        success: true,
        sheetId: sheet.tabId,
        range,
        cellsWritten: writes.length,
        notes_written: notesWritten.length > 0 ? notesWritten : undefined,
        notes_appended: notesAppended.length > 0 ? notesAppended : undefined,
        formula_results: Object.keys(formulaResults).length > 0 ? formulaResults : undefined,
        formula_results_truncated: seedFormulaResultsTruncated || copiedFormulaResultsTruncated || undefined,
      };
    }, { label: 'set_cell_range' });
  }

  /**
   * Apply width/height size specs to a range's format. Shared by setCellRange
   * and resizeRange. A spec is { type: 'points'|'standard', value? }.
   * @param {Excel.Range} range - Target range
   * @param {Object} [width] - Column width spec
   * @param {Object} [height] - Row height spec
   */
  _applyResize(range, width, height) {
    if (width) {
      if (width.type === 'points' && width.value !== undefined) {
        range.format.columnWidth = width.value;
      } else if (width.type === 'standard') {
        range.format.useStandardWidth = true;
      }
    }
    if (height) {
      if (height.type === 'points' && height.value !== undefined) {
        range.format.rowHeight = height.value;
      } else if (height.type === 'standard') {
        range.format.useStandardHeight = true;
      }
    }
  }

  // ===========================================================================
  // TOOL: clear_cell_range
  // ===========================================================================

  /**
   * Clear a range's contents, formats, or both.
   * @param {Object} params
   * @param {number} params.sheetId - tabId of the sheet
   * @param {string} params.range - A1 range to clear
   * @param {string} [params.clearType='contents'] - 'contents' | 'formats' | 'all'
   * @returns {Promise<Object>} - { success }
   */
  async clearCellRange({ sheetId, range, clearType = 'contents' }) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }
    if (!range) {
      throw new Error('range is required');
    }

    return this._safeOfficeRun(async (context) => {
      const sheet = await this._getWorksheetById(context, sheetId);
      const applyTo = Excel.ClearApplyTo || {};
      const clearMap = {
        contents: applyTo.contents != null ? applyTo.contents : 'Contents',
        all: applyTo.all != null ? applyTo.all : 'All',
        formats: applyTo.formats != null ? applyTo.formats : 'Formats',
      };
      sheet.getRange(range).clear(clearMap[clearType] || clearMap.contents);
      await context.sync();
      return { success: true, sheetId: sheet.tabId, range, clearType };
    }, { label: 'clear_cell_range' });
  }

  // ===========================================================================
  // TOOL: copy_to
  // ===========================================================================

  /**
   * Copy a source range to a destination range, with Excel's automatic formula
   * translation and pattern expansion (destination may be larger than source).
   * @param {Object} params
   * @param {number} params.sheetId - tabId of the sheet
   * @param {string} params.sourceRange - A1 source range
   * @param {string} params.destinationRange - A1 destination range
   * @param {boolean} [params.allow_overwrite] - Permit overwriting non-empty destination cells
   * @returns {Promise<Object>} - { success, formula_results? }
   */
  async copyTo({ sheetId, sourceRange, destinationRange, allow_overwrite }) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }
    if (!sourceRange || !destinationRange) {
      throw new Error('sourceRange and destinationRange are required');
    }

    return this._safeOfficeRun(async (context) => {
      const sheet = await this._getWorksheetById(context, sheetId);
      const copyInspection = await this._findCopyConflicts(
        sheet,
        context,
        sourceRange,
        destinationRange,
        !allow_overwrite
      );
      if (copyInspection.conflicts.length > 0) {
        this._throwOverwriteConflicts(copyInspection.conflicts);
      }
      await this._assertCopySourceFormulasSafe(sheet, context, sourceRange);
      sheet.getRange(destinationRange).copyFrom(sourceRange);
      await context.sync();

      await this._calculateRangesBestEffort(
        sheet,
        context,
        [copyInspection.effectiveDestinationRange]
      );
      const copied = await this._readFormulaResults(
        sheet,
        context,
        copyInspection.effectiveDestinationRange
      );
      return {
        success: true,
        sheetId: sheet.tabId,
        sourceRange,
        destinationRange,
        effectiveDestinationRange: copyInspection.effectiveDestinationRange,
        formula_results: Object.keys(copied.results).length > 0 ? copied.results : undefined,
        formula_results_truncated: copied.truncated || undefined,
      };
    }, { label: 'copy_to' });
  }

  // ===========================================================================
  // TOOL: modify_sheet_structure
  // ===========================================================================

  /**
   * Insert, delete, hide, unhide, or freeze rows and columns.
   * @param {Object} params
   * @param {number} params.sheetId - tabId of the sheet
   * @param {string} params.operation - insert | delete | hide | unhide | freeze | unfreeze
   * @param {string} [params.dimension] - 'rows' | 'columns' (required except unfreeze)
   * @param {string} [params.reference] - Row number ('5') or column letter ('C')
   * @param {string} [params.position='before'] - 'before' | 'after' (insert only)
   * @param {number} [params.count=1] - Number of rows/columns to operate on
   * @returns {Promise<Object>} - { success }
   */
  async modifySheetStructure({ sheetId, operation, dimension, reference, position = 'before', count = 1 }) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const sheet = await this._getWorksheetById(context, sheetId);
      const n = count || 1;
      const insertShift = Excel.InsertShiftDirection || {};
      const deleteShift = Excel.DeleteShiftDirection || {};

      if (operation === 'unfreeze') {
        sheet.freezePanes.unfreeze();
      } else if (dimension === 'rows') {
        if (operation === 'freeze') {
          sheet.freezePanes.freezeRows(n);
        } else {
          if (!reference) throw new Error('reference is required for insert/delete/hide/unhide on rows');
          const refRow = parseInt(reference, 10);
          if (!Number.isInteger(refRow)) throw new Error(`Invalid row reference: ${reference}`);
          const startRow = position === 'after' ? refRow + 1 : refRow;
          const endRow = startRow + n - 1;
          const rangeRows = sheet.getRange(`${startRow}:${endRow}`);

          switch (operation) {
            case 'insert':
              rangeRows.insert(insertShift.down != null ? insertShift.down : 'Down');
              break;
            case 'delete':
              rangeRows.delete(deleteShift.up != null ? deleteShift.up : 'Up');
              break;
            case 'hide':
              rangeRows.rowHidden = true;
              break;
            case 'unhide':
              rangeRows.rowHidden = false;
              break;
            default:
              throw new Error(`Unknown operation: ${operation}`);
          }
        }
      } else if (dimension === 'columns') {
        if (operation === 'freeze') {
          sheet.freezePanes.freezeColumns(n);
        } else {
          if (!reference) throw new Error('reference is required for insert/delete/hide/unhide on columns');
          const colNum = letterToColumn(reference.toUpperCase());
          if (!colNum) throw new Error(`Invalid column reference: ${reference}`);
          const startCol = position === 'after' ? colNum + 1 : colNum;
          const endCol = startCol + n - 1;
          const rangeCols = sheet.getRange(`${columnToLetter(startCol)}:${columnToLetter(endCol)}`);

          switch (operation) {
            case 'insert':
              rangeCols.insert(insertShift.right != null ? insertShift.right : 'Right');
              break;
            case 'delete':
              rangeCols.delete(deleteShift.left != null ? deleteShift.left : 'Left');
              break;
            case 'hide':
              rangeCols.columnHidden = true;
              break;
            case 'unhide':
              rangeCols.columnHidden = false;
              break;
            default:
              throw new Error(`Unknown operation: ${operation}`);
          }
        }
      } else {
        throw new Error('dimension (rows | columns) is required for this operation');
      }

      await context.sync();
      return { success: true, sheetId: sheet.tabId, operation, dimension };
    }, { label: 'modify_sheet_structure' });
  }

  // ===========================================================================
  // TOOL: modify_workbook_structure
  // ===========================================================================

  /**
   * Create, delete, rename, or duplicate worksheets.
   * @param {Object} params
   * @param {string} params.operation - create | delete | rename | duplicate
   * @param {string} [params.sheetName] - Name for a new sheet (create)
   * @param {number} [params.rows=1000] - Echoed back for create (Excel grid is fixed)
   * @param {number} [params.columns=26] - Echoed back for create
   * @param {string} [params.tabColor] - Tab color hex (create)
   * @param {number} [params.sheetId] - tabId (delete/rename/duplicate)
   * @param {string} [params.newName] - New name (rename, optional for duplicate)
   * @returns {Promise<Object>} - { success, sheetId?, message }
   */
  async modifyWorkbookStructure({ operation, sheetName, rows = 1000, columns = 26, tabColor, sheetId, newName }) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }

    return this._safeOfficeRun(async (context) => {
      switch (operation) {
        case 'create': {
          const name = sheetName || 'Sheet';
          const newSheet = context.workbook.worksheets.add(name);
          newSheet.load('tabId,name');
          await context.sync();
          if (tabColor) {
            newSheet.tabColor = tabColor;
            await context.sync();
          }
          return {
            success: true,
            sheetId: newSheet.tabId,
            sheetName: newSheet.name,
            rows,
            columns,
            message: `Created new sheet "${newSheet.name}"`,
          };
        }

        case 'delete': {
          if (sheetId == null) throw new Error('sheetId is required for delete');
          const sheet = await this._getWorksheetById(context, sheetId);
          sheet.delete();
          await context.sync();
          return { success: true, message: `Deleted sheet with ID ${sheetId}` };
        }

        case 'rename': {
          if (sheetId == null || !newName) throw new Error('sheetId and newName are required for rename');
          const sheet = await this._getWorksheetById(context, sheetId);
          sheet.name = newName;
          await context.sync();
          return { success: true, sheetId: sheet.tabId, sheetName: newName, message: `Renamed sheet to "${newName}"` };
        }

        case 'duplicate': {
          if (sheetId == null) throw new Error('sheetId is required for duplicate');
          const source = await this._getWorksheetById(context, sheetId);
          source.load('name');
          await context.sync();

          const copyName = newName || `${source.name} Copy`;
          const newSheet = context.workbook.worksheets.add(copyName);
          newSheet.load('tabId,name');

          const usedRange = source.getUsedRangeOrNullObject(true);
          usedRange.load('values,formulas,isNullObject');
          await context.sync();

          if (!usedRange.isNullObject && usedRange.formulas.length > 0) {
            const dest = newSheet.getRangeByIndexes(
              0, 0,
              usedRange.formulas.length,
              usedRange.formulas[0].length
            );
            dest.formulas = usedRange.formulas;
            await context.sync();
          }

          return {
            success: true,
            sheetId: newSheet.tabId,
            sheetName: copyName,
            message: `Duplicated sheet as "${copyName}"`,
          };
        }

        default:
          throw new Error(`Unknown operation: ${operation}`);
      }
    }, { label: 'modify_workbook_structure' });
  }

  // ===========================================================================
  // TOOL: resize_range
  // ===========================================================================

  /**
   * Resize columns and/or rows in a range (or the whole sheet if range omitted).
   * @param {Object} params
   * @param {number} params.sheetId - tabId of the sheet
   * @param {string} [params.range] - A1 range; whole sheet if omitted
   * @param {Object} [params.width] - Column width spec { type, value? }
   * @param {Object} [params.height] - Row height spec { type, value? }
   * @returns {Promise<Object>} - { success }
   */
  async resizeRange({ sheetId, range, width, height }) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const sheet = await this._getWorksheetById(context, sheetId);
      const target = range ? sheet.getRange(range) : sheet.getRange();
      this._applyResize(target, width, height);
      await context.sync();
      return { success: true, sheetId: sheet.tabId, range: range || null };
    }, { label: 'resize_range' });
  }

  // ===========================================================================
  // TOOL: modify_object (charts + pivot tables)
  // ===========================================================================

  /**
   * Create, update, or delete a chart or pivot table. Dynamically dispatches
   * over { create, update, delete } x { chart, pivotTable } against the native
   * Office.js API (worksheet.charts.add / worksheet.pivotTables.add).
   *
   * Immutability rule: a chart/pivot's source range or anchor cannot be edited
   * in place. To change those, delete the object FIRST and recreate it (the
   * model is expected to issue the delete before the create).
   *
   * @param {Object} params
   * @param {string} params.operation - create | update | delete
   * @param {number} params.sheetId - tabId of the sheet holding the object
   * @param {string} params.objectType - 'chart' | 'pivotTable'
   * @param {string} [params.id] - Object id (required for update/delete)
   * @param {Object} [params.properties] - Object properties (create/update)
   * @returns {Promise<Object>} - { success, id?, messages? }
   */
  async modifyObject({ operation, sheetId, objectType, id, properties }) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }
    if (!operation || !objectType) {
      throw new Error('operation and objectType are required');
    }
    if (sheetId == null) {
      throw new Error('sheetId is required');
    }

    return this._safeOfficeRun(async (context) => {
      const props = properties || {};

      if (objectType === 'chart') {
        return this._modifyChart(context, operation, sheetId, id, props);
      }
      if (objectType === 'pivotTable') {
        return this._modifyPivotTable(context, operation, sheetId, id, props);
      }
      throw new Error(`Unknown objectType: ${objectType}`);
    }, { label: 'modify_object' });
  }

  /**
   * Resolve a "SheetName!A1:B10" source address to an Excel.Range. Looks the
   * worksheet up by name (the address carries a name, not a tabId).
   * @param {Excel.RequestContext} context - The active request context
   * @param {string} address - Source address including a sheet name
   * @returns {Promise<Excel.Range>}
   */
  async _resolveSourceRange(context, address) {
    const bangIdx = String(address || '').lastIndexOf('!');
    if (bangIdx === -1) {
      throw new Error(`source must include a sheet name (e.g. "Sheet1!A1:D100"), got: ${address}`);
    }
    let sheetName = address.slice(0, bangIdx);
    const rangePart = address.slice(bangIdx + 1);
    if (sheetName.startsWith("'") && sheetName.endsWith("'")) {
      sheetName = sheetName.slice(1, -1).replace(/''/g, "'");
    }
    const sheet = await this._getWorksheetById(context, sheetName);
    return sheet.getRange(rangePart);
  }

  /**
   * create/update/delete dispatch for charts (native Excel.Chart API).
   * @param {Excel.RequestContext} context - The active request context
   * @param {string} operation - create | update | delete
   * @param {number} sheetId - tabId of the host sheet
   * @param {string} [id] - Chart id (update/delete)
   * @param {Object} props - Chart properties
   */
  async _modifyChart(context, operation, sheetId, id, props) {
    const sheet = await this._getWorksheetById(context, sheetId);

    if (operation === 'create') {
      if (!props.source) throw new Error('properties.source is required to create a chart');
      const sourceRange = await this._resolveSourceRange(context, props.source);
      const seriesBy = (Excel.ChartSeriesBy && Excel.ChartSeriesBy.auto) || 'Auto';
      const chart = sheet.charts.add(mapChartType(props.chartType), sourceRange, seriesBy);

      if (props.anchor) chart.setPosition(props.anchor);
      if (props.title != null) chart.title.text = props.title;

      chart.load('id');
      await context.sync();
      return { success: true, id: chart.id };
    }

    if (operation === 'delete') {
      if (!id) throw new Error('id is required to delete a chart');
      const chart = this._findInCollection(sheet.charts, 'id', id, context, 'Chart');
      const resolved = await chart;
      resolved.delete();
      await context.sync();
      return { success: true };
    }

    if (operation === 'update') {
      if (!id) throw new Error('id is required to update a chart');
      const chart = await this._findInCollection(sheet.charts, 'id', id, context, 'Chart');

      if (props.chartType) chart.chartType = mapChartType(props.chartType);
      if (props.title != null) chart.title.text = props.title;
      if (props.anchor) chart.setPosition(props.anchor);
      if (props.position) {
        if (props.position.top != null) chart.top = props.position.top;
        if (props.position.left != null) chart.left = props.position.left;
      }
      // Immutability: changing the source range in place is not supported by the
      // tool contract. To repoint a chart, delete it and recreate with the new
      // source. We intentionally do not call chart.setData here.
      // TODO(phase-2): advanced chart properties (series styling, axes,
      // legend, data labels) once the tool surface grows.

      await context.sync();
      return { success: true, id };
    }

    throw new Error(`Unknown operation: ${operation}`);
  }

  /**
   * create/update/delete dispatch for pivot tables (native Excel.PivotTable API).
   * @param {Excel.RequestContext} context - The active request context
   * @param {string} operation - create | update | delete
   * @param {number} sheetId - tabId of the host sheet
   * @param {string} [id] - Pivot table id (update/delete)
   * @param {Object} props - Pivot table properties
   */
  async _modifyPivotTable(context, operation, sheetId, id, props) {
    const sheet = await this._getWorksheetById(context, sheetId);

    if (operation === 'create') {
      if (!props.source) throw new Error('properties.source is required to create a pivot table');
      if (!props.range) throw new Error('properties.range is required to create a pivot table');
      const sourceRange = await this._resolveSourceRange(context, props.source);
      const destRange = sheet.getRange(props.range);

      const pivot = sheet.pivotTables.add(props.name || 'PivotTable', sourceRange, destRange);
      this._configurePivotFields(pivot, props);

      pivot.load('id');
      await context.sync();
      return { success: true, id: pivot.id };
    }

    if (operation === 'delete') {
      if (!id) throw new Error('id is required to delete a pivot table');
      const pivot = await this._findInCollection(sheet.pivotTables, 'id', id, context, 'Pivot table');
      pivot.delete();
      await context.sync();
      return { success: true };
    }

    if (operation === 'update') {
      if (!id) throw new Error('id is required to update a pivot table');
      const pivot = await this._findInCollection(sheet.pivotTables, 'id', id, context, 'Pivot table');

      if (props.name) pivot.name = props.name;

      // Immutability: source/destination changes require delete + recreate.
      // For field changes we clear the existing hierarchies, then re-add.
      // TODO(phase-2): granular field add/remove/reorder and filters.
      if (props.rows?.length || props.columns?.length || props.values?.length) {
        pivot.rowHierarchies.load('items');
        pivot.columnHierarchies.load('items');
        pivot.dataHierarchies.load('items');
        await context.sync();

        if (props.rows?.length) {
          for (const h of pivot.rowHierarchies.items) pivot.rowHierarchies.remove(h);
        }
        if (props.columns?.length) {
          for (const h of pivot.columnHierarchies.items) pivot.columnHierarchies.remove(h);
        }
        if (props.values?.length) {
          for (const h of pivot.dataHierarchies.items) pivot.dataHierarchies.remove(h);
        }
        await context.sync();

        this._configurePivotFields(pivot, props);
        pivot.refresh();
      }

      await context.sync();
      return { success: true, id };
    }

    throw new Error(`Unknown operation: ${operation}`);
  }

  /**
   * Add row/column/value hierarchies to a pivot table from a properties object.
   * Mirrors the reverse-engineered configurePivotFields.
   * @param {Excel.PivotTable} pivot - The pivot table to configure
   * @param {Object} config - { rows?, columns?, values? }
   */
  _configurePivotFields(pivot, config) {
    for (const row of config.rows || []) {
      pivot.rowHierarchies.add(pivot.hierarchies.getItem(row.field));
    }
    for (const col of config.columns || []) {
      pivot.columnHierarchies.add(pivot.hierarchies.getItem(col.field));
    }
    for (const val of config.values || []) {
      const dataHierarchy = pivot.dataHierarchies.add(pivot.hierarchies.getItem(val.field));
      if (val.summarizeBy) {
        dataHierarchy.summarizeBy = mapSummarizeBy(val.summarizeBy);
      }
    }
  }

  /**
   * Find an item in a chart/pivot collection by a property (typically id),
   * loading the collection first and throwing a clear error if absent.
   * @param {Object} collection - A chart or pivotTable collection
   * @param {string} key - Property to match on (e.g. 'id')
   * @param {string} value - Value to match
   * @param {Excel.RequestContext} context - The active request context
   * @param {string} label - Human label for error messages
   * @returns {Promise<Object>} The matching item proxy
   */
  async _findInCollection(collection, key, value, context, label) {
    collection.load(`items/${key}`);
    await context.sync();
    const item = collection.items.find((it) => it[key] === value);
    if (!item) {
      throw new Error(`NotFoundError: ${label} with ${key} ${value} not found on this sheet`);
    }
    return item;
  }

  // ===========================================================================
  // CONTEXT: getInitialState (workbook metadata for context injection)
  // ===========================================================================

  /**
   * Gather workbook metadata sent with every user message as <initial_state>.
   * Parallel to PowerPointBridge.getInitialState: returns null on any failure
   * rather than throwing, so context injection degrades gracefully.
   * @returns {Promise<Object|null>} - Workbook metadata, or null
   */
  async getInitialState() {
    if (typeof Excel === 'undefined') {
      return null;
    }

    try {
      return await this._safeOfficeRun(async (context) => {
        const workbook = context.workbook;
        const worksheets = workbook.worksheets;
        workbook.load('name');
        worksheets.load('items/name,items/tabId');
        await context.sync();

        const sheetsMetadata = [];
        for (const ws of worksheets.items) {
          const meta = await this._getSheetMetadata(ws, context);
          sheetsMetadata.push({
            sheetId: meta.sheetId,
            name: meta.name,
            dimension: { rows: meta.rows, cols: meta.cols },
            frozenPanes: { rows: meta.frozenRows, cols: meta.frozenCols },
          });
        }

        const docUrl = (typeof Office !== 'undefined' && Office.context?.document?.url) || '';
        const fileNameFromUrl = docUrl ? (docUrl.split('/').pop().split('?')[0] || '') : '';
        const fileName = workbook.name || fileNameFromUrl || 'Workbook';

        return {
          fileName,
          totalSheets: sheetsMetadata.length,
          sheetsMetadata,
          displayLanguage: typeof Office !== 'undefined' ? (Office.context?.displayLanguage || null) : null,
        };
      });
    } catch (error) {
      console.warn('[ExcelBridge] getInitialState failed:', error.message);
      return null;
    }
  }

  /**
   * Write a bounded licensed-data table to an explicit worksheet range. This
   * path accepts values only, protects populated cells by default, and reads
   * every written cell back before returning safe location metadata.
   */
  async writeFinanceExcelTable(args, options = {}) {
    const request = validateFinanceExcelTableRequest(args);
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const sheet = context.workbook.worksheets.getItem(request.target.sheetName);
      const range = sheet.getRange(request.rangeAddress);
      sheet.load('name');
      range.load('address,rowCount,columnCount,values,formulas');
      await context.sync();

      if (
        sheet.name !== request.target.sheetName ||
        range.rowCount !== request.rowCount ||
        range.columnCount !== request.columnCount
      ) {
        throw new Error('Finance Excel table target could not be resolved exactly');
      }
      if (!request.allowOverwrite) {
        const populated = range.values.some((row, rowIndex) => row.some((value, columnIndex) => {
          const formula = range.formulas?.[rowIndex]?.[columnIndex];
          return (value !== '' && value !== null && value !== undefined) ||
            (formula !== '' && formula !== null && formula !== undefined);
        }));
        if (populated) {
          throw new Error('Finance Excel table would overwrite populated cells');
        }
      }

      const applyTo = Excel.ClearApplyTo || {};
      range.clear(applyTo.contents != null ? applyTo.contents : 'Contents');
      range.values = request.cells.map((row) => row.map((value) => value === null ? '' : value));
      await context.sync();

      range.load('values,formulas,rowCount,columnCount');
      await context.sync();
      const verified = range.rowCount === request.rowCount &&
        range.columnCount === request.columnCount &&
        request.cells.every((row, rowIndex) => row.every((expected, columnIndex) => {
          const formula = range.formulas?.[rowIndex]?.[columnIndex];
          return !(typeof formula === 'string' && formula.startsWith('=')) &&
            excelScalarMatches(expected, range.values?.[rowIndex]?.[columnIndex]);
        }));
      if (!verified) {
        throw new Error('Finance Excel table verification failed');
      }

      return {
        success: true,
        verifiedNativeWrite: true,
        writtenRange: `${request.target.sheetName}!${request.rangeAddress}`,
      };
    }, { label: 'write_finance_excel_table', ...options });
  }

  /**
   * Attach code-issued Daloopa source markers to an explicit cell as a native
   * Excel comment. Existing user comment text is preserved, and the comment is
   * read back before the bridge acknowledges success.
   */
  async attachConnectorCitations({ citations, target }, options = {}) {
    const safeCitations = validateDaloopaCitations(citations);
    const safeTarget = validateDaloopaExcelTarget(target);
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }

    return this._safeOfficeRun(async (context) => {
      const workbook = context.workbook;
      const sheet = workbook.worksheets.getItem(safeTarget.sheetName);
      const targetCell = sheet.getRange(safeTarget.anchorCell);
      sheet.load('name');
      targetCell.load('address');
      await context.sync();

      let threads = await this._loadCommentThreadsByCell(sheet, context);
      let thread = threads.get(safeTarget.anchorCell);
      const existingLength = thread
        ? [thread.rootContent, ...thread.replyContents].join('\n\n').length
        : 0;
      const pendingCitations = safeCitations.filter(
        (citation) => !commentThreadHasExactContent(thread, citation.marker)
      );
      if (existingLength + pendingCitations.reduce((sum, citation) => sum + citation.marker.length, 0) > 32_000) {
        throw new Error('The target Excel cell source comment is full');
      }

      if (!thread && pendingCitations.length > 0) {
        sheet.comments.add(safeTarget.anchorCell, pendingCitations.shift().marker);
        await context.sync();
        threads = await this._loadCommentThreadsByCell(sheet, context);
        thread = threads.get(safeTarget.anchorCell);
        if (!thread) throw new Error('Daloopa source marker could not be created in Excel');
      }
      const knownContents = new Set(
        thread
          ? [thread.rootContent, ...thread.replyContents].map(normalizeCommentContent)
          : []
      );
      for (const citation of pendingCitations) {
        const marker = normalizeCommentContent(citation.marker);
        if (!knownContents.has(marker)) {
          thread.comment.replies.add(citation.marker);
          knownContents.add(marker);
        }
      }
      if (pendingCitations.length > 0) await context.sync();

      const verifiedThread = (await this._loadCommentThreadsByCell(sheet, context))
        .get(safeTarget.anchorCell);
      if (safeCitations.some((citation) => !commentThreadHasExactContent(verifiedThread, citation.marker))) {
        throw new Error('Daloopa source marker verification failed in Excel');
      }

      return {
        success: true,
        attachedCitationIds: safeCitations.map((citation) => citation.citationId),
      };
    }, { label: 'attach_connector_citations', ...options });
  }

  /**
   * Render a worksheet range as a base64 PNG for visual verification.
   * Excel.Range.getImage returns a ClientResult whose value is populated after
   * context.sync().
   */
  async screenshotRange({ sheetId, range }) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }
    if (!range || typeof range !== 'string') {
      throw new Error('screenshot_range requires an A1 range');
    }

    return this._safeOfficeRun(async (context) => {
      const sheet = await this._getWorksheetById(context, sheetId);
      const target = sheet.getRange(range);
      const image = target.getImage();
      await context.sync();
      if (!image.value) throw new Error('Excel returned an empty range image');
      return {
        content: [
          { type: 'text', text: `Rendered ${sheet.name}!${range} for visual review.` },
          {
            type: 'image',
            source: {
              type: 'base64',
              media_type: 'image/png',
              data: image.value,
            },
          },
        ],
      };
    });
  }

  // ===========================================================================
  // CONTEXT: getUserContext (active sheet + current selection)
  // ===========================================================================

  /**
   * Get the active sheet and current selection for <user_context> injection.
   * @returns {Promise<Object|null>} - Selection state, or null
   */
  async getUserContext() {
    if (typeof Excel === 'undefined') {
      return null;
    }

    // Office on the web: skip the selection probe. It is a non-essential
    // <user_context> nicety, but it runs through the SAME serialized Excel.run
    // queue as the real tools. On the web host this probe's Excel.run can hang
    // (observed: 120s timeout), and because runs are serialized, that hang
    // poisons the queue so every subsequent tool call times out waiting behind
    // it. Skipping it on the web gives the first real tool run a clean,
    // unblocked shot at the host.
    if (this._isOfficeOnline) {
      console.log('[ExcelBridge] getUserContext skipped on Office web (avoids run-queue stall)');
      return null;
    }

    try {
      return await this._safeOfficeRun(async (context) => {
        const activeSheet = context.workbook.worksheets.getActiveWorksheet();
        activeSheet.load('tabId,name');

        const selectedRanges = context.workbook.getSelectedRanges();
        selectedRanges.load('address');
        await context.sync();

        const ranges = selectedRanges.address
          ? selectedRanges.address.split(',').map((a) => a.trim()).filter(Boolean)
          : [];

        return {
          activeSheetId: activeSheet.tabId,
          activeSheetName: activeSheet.name,
          selectedRanges: ranges,
        };
      });
    } catch (error) {
      console.warn('[ExcelBridge] getUserContext failed:', error.message);
      return null;
    }
  }

  /**
   * apply_template: insert the worksheets of a template .xlsx/.xltx (base64)
   * into the open workbook with their formatting intact, so the agent can
   * build on styled sheets instead of blank ones.
   */
  async applyTemplateFromBase64(base64) {
    if (typeof Excel === 'undefined') {
      throw new Error('Excel API not available');
    }
    if (typeof Office === 'undefined' || !Office?.context?.requirements?.isSetSupported?.('ExcelApi', '1.13')) {
      throw new Error('This Excel version does not support inserting sheets from a file (requires ExcelApi 1.13).');
    }

    return this._safeOfficeRun(async (context) => {
      const workbook = context.workbook;
      workbook.insertWorksheetsFromBase64(base64);
      await context.sync();

      const sheets = workbook.worksheets;
      sheets.load('items/name');
      await context.sync();

      return {
        success: true,
        sheets: sheets.items.map((s) => s.name),
        note: 'Template sheets inserted with their formatting. Build on these sheets rather than blank ones.',
      };
    }, { label: 'apply_template', timeoutMs: 120000 });
  }

}
